<template>
  <div
    class="page-container"
    :class="{ 'page-container--map-ui-hidden': mapFullscreen }"
  >
    <PageHeader
      v-show="!mapFullscreen"
      icon="fuel-planner-active.svg"
      title="Топливный планировщик"
    />
    <div
      class="page-content fuel-planner"
      :class="{
        'fuel-planner--map-expanded': mapFullscreen,
        'fuel-planner--region-browse': plannerMode === 'region' && !mapFullscreen,
      }"
    >
      <Loader v-if="!isPageReady" />
      <aside
        v-show="!mapFullscreen || mapFormVisible"
        class="fuel-planner__form"
        :class="{ 'fuel-planner__form--expanded-overlay': mapFullscreen && mapFormVisible }"
      >
        <div class="fuel-planner__mode-tabs" role="tablist" aria-label="Режим планировщика">
          <button
            type="button"
            role="tab"
            class="fuel-planner__mode-tab"
            :class="{ 'fuel-planner__mode-tab--active': plannerMode === 'route' }"
            :aria-selected="plannerMode === 'route'"
            :disabled="isBuilding || isLoadingStations || isLoadingRegion"
            @click="setPlannerMode('route')"
          >
            Маршрут
          </button>
          <button
            type="button"
            role="tab"
            class="fuel-planner__mode-tab"
            :class="{ 'fuel-planner__mode-tab--active': plannerMode === 'region' }"
            :aria-selected="plannerMode === 'region'"
            :disabled="isBuilding || isLoadingStations || isLoadingRegion"
            @click="setPlannerMode('region')"
          >
            По региону
          </button>
        </div>

        <template v-if="plannerMode === 'route'">
        <div ref="pointsScroll" class="fuel-planner__points">
          <label class="input-wrap fuel-planner__suggest-wrap">
            <span>Точка А</span>
            <input
              ref="inputA"
              v-model="pointA"
              type="text"
              data-suggest-point="A"
              placeholder="Адрес или населённый пункт"
              autocomplete="off"
              @input="onPointInput('A')"
              @focus="onPointFocus('A')"
              @blur="onPointBlur('A')"
            />
            <Teleport to="body">
              <ul
                v-if="suggestA.open && suggestA.items.length"
                class="fuel-planner__suggest-list"
                :style="suggestA.anchorStyle"
                @mousedown.prevent
              >
                <li
                  v-for="(item, idx) in suggestA.items"
                  :key="'a-' + idx + '-' + item.value"
                  class="fuel-planner__suggest-item"
                  @mousedown.prevent="applySuggest('A', item)"
                >
                  {{ item.displayName || item.value }}
                </li>
              </ul>
            </Teleport>
          </label>

          <div
            v-for="(mid, midIdx) in midPoints"
            :key="mid.key"
            class="fuel-planner__mid-row"
          >
            <label class="input-wrap fuel-planner__suggest-wrap fuel-planner__mid-input">
              <span>Точка {{ midPointLabel(midIdx) }}</span>
              <input
                v-model="mid.text"
                type="text"
                :data-suggest-point="mid.key"
                placeholder="Адрес или населённый пункт"
                autocomplete="off"
                @input="onPointInput(mid.key)"
                @focus="onPointFocus(mid.key)"
                @blur="onPointBlur(mid.key)"
              />
              <Teleport to="body">
                <ul
                  v-if="mid.suggest.open && mid.suggest.items.length"
                  class="fuel-planner__suggest-list"
                  :style="mid.suggest.anchorStyle"
                  @mousedown.prevent
                >
                  <li
                    v-for="(item, idx) in mid.suggest.items"
                    :key="mid.key + '-' + idx + '-' + item.value"
                    class="fuel-planner__suggest-item"
                    @mousedown.prevent="applySuggest(mid.key, item)"
                  >
                    {{ item.displayName || item.value }}
                  </li>
                </ul>
              </Teleport>
            </label>
            <button
              type="button"
              class="fuel-planner__mid-remove"
              title="Удалить точку"
              aria-label="Удалить точку"
              :disabled="isBuilding || isLoadingStations"
              @click="removeMidPoint(mid.key)"
            >
              ×
            </button>
          </div>

          <div class="fuel-planner__mid-row">
            <label
              class="input-wrap fuel-planner__suggest-wrap fuel-planner__mid-input"
            >
              <span>Точка {{ destinationLabel }}</span>
              <input
                ref="inputB"
                v-model="pointB"
                type="text"
                data-suggest-point="B"
                placeholder="Адрес или населённый пункт"
                autocomplete="off"
                @input="onPointInput('B')"
                @focus="onPointFocus('B')"
                @blur="onPointBlur('B')"
              />
              <Teleport to="body">
                <ul
                  v-if="suggestB.open && suggestB.items.length"
                  class="fuel-planner__suggest-list"
                  :style="suggestB.anchorStyle"
                  @mousedown.prevent
                >
                  <li
                    v-for="(item, idx) in suggestB.items"
                    :key="'b-' + idx + '-' + item.value"
                    class="fuel-planner__suggest-item"
                    @mousedown.prevent="applySuggest('B', item)"
                  >
                    {{ item.displayName || item.value }}
                  </li>
                </ul>
              </Teleport>
            </label>
            <button
              v-if="canRemoveDestination"
              type="button"
              class="fuel-planner__mid-remove"
              title="Удалить точку"
              aria-label="Удалить точку"
              :disabled="isBuilding || isLoadingStations"
              @click="removeDestinationPoint"
            >
              ×
            </button>
            <button
              type="button"
              class="fuel-planner__mid-add"
              :disabled="!canAddMidPoint || isBuilding || isLoadingStations"
              :title="addMidPointTitle"
              aria-label="Добавить точку"
              @click="addMidPoint"
            >
              +
            </button>
          </div>
        </div>

        <p v-if="!hasDadataKey" class="fuel-planner__hint">
          Укажите VUE_APP_DADATA_API_KEY в .env и перезапустите npm run serve
        </p>

        <p v-if="isLoadingStations" class="fuel-planner__hint">Ищем АЗС…</p>

        <div class="fuel-planner__actions">
          <button
            type="button"
            class="btn-primary"
            :disabled="isBuilding || isLoadingStations || !canBuildRoute"
            :title="buildDisabledTitle"
            @click="buildRoute"
          >
            {{ isBuilding ? 'Строим…' : 'Построить маршрут' }}
          </button>
          <div class="fuel-planner__actions-row">
            <button
              type="button"
              class="btn-primary"
              :disabled="!canOpenInYandexMaps"
              :title="canOpenInYandexMaps ? '' : 'Сначала постройте маршрут'"
              @click="copyYandexMapsRouteLink"
            >
              {{ yandexLinkCopied ? 'Скопировано' : 'Скопировать ссылку' }}
            </button>
            <button
              type="button"
              class="btn-primary"
              :disabled="!canOpenInYandexMaps"
              :title="canOpenInYandexMaps ? '' : 'Сначала постройте маршрут'"
              @click="openInYandexMaps"
            >
              Открыть в Яндекс.Картах
            </button>
          </div>
        </div>

        <div
          v-if="routeBuilt && routeAlternatives.length > 1"
          class="fuel-planner__route-alts"
          role="listbox"
          aria-label="Варианты маршрута"
        >
          <button
            v-for="(alt, idx) in routeAlternatives"
            :key="'route-alt-' + idx"
            type="button"
            class="fuel-planner__route-alt"
            :class="{
              'fuel-planner__route-alt--selected': idx === selectedRouteIndex,
            }"
            role="option"
            :aria-selected="idx === selectedRouteIndex"
            :disabled="isBuilding || isUpdatingAlternativeStations"
            @click="selectRouteAlternative(idx)"
          >
            {{ routeAlternativeLabel(idx, alt) }}
          </button>
        </div>
        <p v-if="isUpdatingAlternativeStations" class="fuel-planner__hint">
          Обновляем АЗС...
        </p>
        </template>

        <div v-else class="fuel-planner__region-panel">
          <div class="input-wrap fuel-planner__region-select">
            <span>Регионы РФ</span>
            <v-select
              v-model="selectedRegionOptions"
              class="fuel-planner__region-vselect"
              :options="regionOptions"
              label="name"
              :multiple="true"
              :clearable="true"
              :searchable="true"
              :close-on-select="false"
              :deselect-from-dropdown="true"
              placeholder="Выберите регионы"
            />
          </div>
          <p v-if="isLoadingRegion" class="fuel-planner__hint">Загружаем АЗС…</p>
          <p
            v-else-if="!selectedRegionIds.length && !selectedBrandFilters.length"
            class="fuel-planner__hint"
          >
            Выберите регионы и/или бренды, чтобы показать АЗС на карте
          </p>
          <p v-else-if="regionStationsLoaded" class="fuel-planner__hint">
            Найдено АЗС: {{ stations.length }}
          </p>
        </div>

        <div class="fuel-planner__found">
          <div
            v-if="showBrandFilter || showFuelTypeFilter"
            class="fuel-planner__filters-row"
          >
              <div
                v-if="showBrandFilter"
                class="input-wrap fuel-planner__brand-filter fuel-planner__brand-filter--searchable"
              >
                <span>Бренды</span>
                <div
                  class="fuel-planner__filter-dropdown"
                  @click.stop
                  @mousedown.stop
                >
                  <div
                    class="fuel-planner__filter-trigger"
                    :class="{
                      'fuel-planner__filter-trigger--open': brandFilterOpen,
                    }"
                    @click="onBrandFilterTriggerClick"
                  >
                    <span
                      v-show="showBrandFilterTriggerLabel"
                      class="fuel-planner__filter-trigger-label"
                      :class="{
                        'fuel-planner__filter-trigger-label--placeholder':
                          !selectedBrandFilters.length,
                      }"
                    >
                      {{ brandFilterLabel }}
                    </span>
                    <input
                      ref="brandFilterSearchInput"
                      v-model="brandFilterSearch"
                      type="text"
                      class="fuel-planner__filter-trigger-input"
                      :class="{
                        'fuel-planner__filter-trigger-input--active':
                          !showBrandFilterTriggerLabel,
                      }"
                      :placeholder="
                        showBrandFilterTriggerLabel ? '' : 'Поиск бренда'
                      "
                      autocomplete="off"
                      aria-label="Поиск бренда"
                      @focus="onBrandFilterInputFocus"
                      @click.stop
                      @keydown.stop
                      @keydown.escape.stop="closeBrandFilterFromInput"
                    />
                    <span
                      class="fuel-planner__filter-chevron"
                      aria-hidden="true"
                      @click.stop="toggleBrandFilterOpen"
                    />
                  </div>
                  <ul
                    v-if="brandFilterOpen"
                    class="fuel-planner__filter-menu"
                    @click.stop
                  >
                    <li
                      v-if="hasVisibleBrandFilterItems"
                      class="fuel-planner__filter-menu-item"
                      @mousedown.prevent
                      @click.stop="toggleAllBrandFilters"
                    >
                      <span
                        class="fuel-planner__brand-option"
                        :class="{
                          'fuel-planner__brand-option--selected': isAllBrandsSelected,
                        }"
                      >
                        <span
                          class="fuel-planner__brand-option-checkbox"
                          aria-hidden="true"
                        >
                          <svg
                            v-if="isAllBrandsSelected"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                        <span class="fuel-planner__brand-option-label">Все</span>
                      </span>
                    </li>
                    <li
                      v-for="brand in filteredPinnedBrandOptions"
                      :key="brand"
                      class="fuel-planner__filter-menu-item"
                      @mousedown.prevent
                      @click.stop="toggleBrandFilter(brand)"
                    >
                      <span
                        class="fuel-planner__brand-option"
                        :class="{
                          'fuel-planner__brand-option--selected':
                            isBrandOptionSelected(brand),
                        }"
                      >
                        <span
                          class="fuel-planner__brand-option-checkbox"
                          aria-hidden="true"
                        >
                          <svg
                            v-if="isBrandOptionSelected(brand)"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                        <span class="fuel-planner__brand-option-label">{{
                          brand
                        }}</span>
                      </span>
                    </li>
                    <li
                      v-if="filteredSmartOilBrandOptions.length"
                      class="fuel-planner__filter-menu-item"
                      @mousedown.prevent
                      @click.stop="toggleSmartOilBrandsExpanded"
                    >
                      <span
                        class="fuel-planner__brand-option fuel-planner__brand-option--group"
                        :class="{
                          'fuel-planner__brand-option--selected':
                            isAllSmartOilBrandsSelected,
                          'fuel-planner__brand-option--partial':
                            isSmartOilBrandsPartiallySelected,
                          'fuel-planner__brand-option--expanded':
                            isSmartOilBrandGroupExpanded,
                        }"
                      >
                        <span
                          class="fuel-planner__brand-option-checkbox"
                          aria-hidden="true"
                          @click.stop="toggleAllSmartOilBrandFilters"
                        >
                          <svg
                            v-if="isAllSmartOilBrandsSelected"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          <svg
                            v-else-if="isSmartOilBrandsPartiallySelected"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 4H8"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                            />
                          </svg>
                        </span>
                        <span class="fuel-planner__brand-option-label"
                          >Smart Oil</span
                        >
                        <span
                          class="fuel-planner__brand-group-chevron"
                          aria-hidden="true"
                        />
                      </span>
                    </li>
                    <template
                      v-if="
                        isSmartOilBrandGroupExpanded &&
                        filteredSmartOilBrandOptions.length
                      "
                    >
                      <li
                        class="fuel-planner__filter-menu-item"
                        @mousedown.prevent
                        @click.stop="toggleAllSmartOilBrandFilters"
                      >
                        <span
                          class="fuel-planner__brand-option fuel-planner__brand-option--nested"
                          :class="{
                            'fuel-planner__brand-option--selected':
                              isAllSmartOilBrandsSelected,
                          }"
                        >
                          <span
                            class="fuel-planner__brand-option-checkbox"
                            aria-hidden="true"
                          >
                            <svg
                              v-if="isAllSmartOilBrandsSelected"
                              class="fuel-planner__brand-option-check"
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </span>
                          <span class="fuel-planner__brand-option-label"
                            >Все</span
                          >
                        </span>
                      </li>
                      <li
                        v-for="brand in filteredSmartOilBrandOptions"
                        :key="`smart-oil-${brand}`"
                        class="fuel-planner__filter-menu-item"
                        @mousedown.prevent
                        @click.stop="toggleBrandFilter(brand)"
                      >
                        <span
                          class="fuel-planner__brand-option fuel-planner__brand-option--nested"
                          :class="{
                            'fuel-planner__brand-option--selected':
                              isBrandOptionSelected(brand),
                          }"
                        >
                          <span
                            class="fuel-planner__brand-option-checkbox"
                            aria-hidden="true"
                          >
                            <svg
                              v-if="isBrandOptionSelected(brand)"
                              class="fuel-planner__brand-option-check"
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </span>
                          <span class="fuel-planner__brand-option-label">{{
                            brand
                          }}</span>
                        </span>
                      </li>
                    </template>
                  </ul>
                </div>
              </div>
              <div
                v-if="showFuelTypeFilter"
                class="input-wrap fuel-planner__brand-filter fuel-planner__brand-filter--searchable"
              >
                <span>Тип топлива</span>
                <div
                  class="fuel-planner__filter-dropdown"
                  @click.stop
                  @mousedown.stop
                >
                  <div
                    class="fuel-planner__filter-trigger"
                    :class="{
                      'fuel-planner__filter-trigger--open': fuelTypeFilterOpen,
                    }"
                    @click="onFuelTypeFilterTriggerClick"
                  >
                    <span
                      v-show="showFuelTypeFilterTriggerLabel"
                      class="fuel-planner__filter-trigger-label"
                      :class="{
                        'fuel-planner__filter-trigger-label--placeholder':
                          !selectedFuelTypeFilters.length,
                      }"
                    >
                      {{ fuelTypeFilterLabel }}
                    </span>
                    <input
                      ref="fuelTypeFilterSearchInput"
                      v-model="fuelTypeFilterSearch"
                      type="text"
                      class="fuel-planner__filter-trigger-input"
                      :class="{
                        'fuel-planner__filter-trigger-input--active':
                          !showFuelTypeFilterTriggerLabel,
                      }"
                      :placeholder="
                        showFuelTypeFilterTriggerLabel ? '' : 'Поиск топлива'
                      "
                      autocomplete="off"
                      aria-label="Поиск топлива"
                      @focus="onFuelTypeFilterInputFocus"
                      @click.stop
                      @keydown.stop
                      @keydown.escape.stop="closeFuelTypeFilterFromInput"
                    />
                    <span
                      class="fuel-planner__filter-chevron"
                      aria-hidden="true"
                      @click.stop="toggleFuelTypeFilterOpen"
                    />
                  </div>
                  <ul
                    v-if="fuelTypeFilterOpen"
                    class="fuel-planner__filter-menu"
                    @click.stop
                  >
                    <li
                      v-if="hasVisibleFuelTypeFilterItems"
                      class="fuel-planner__filter-menu-item"
                      @mousedown.prevent
                      @click.stop="toggleAllFuelTypeFilters"
                    >
                      <span
                        class="fuel-planner__brand-option"
                        :class="{
                          'fuel-planner__brand-option--selected': isAllFuelTypesSelected,
                        }"
                      >
                        <span
                          class="fuel-planner__brand-option-checkbox"
                          aria-hidden="true"
                        >
                          <svg
                            v-if="isAllFuelTypesSelected"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                        <span class="fuel-planner__brand-option-label">Все</span>
                      </span>
                    </li>
                    <li
                      v-for="option in filteredFuelTypeOptions"
                      :key="option.value"
                      class="fuel-planner__filter-menu-item"
                      @mousedown.prevent
                      @click.stop="toggleFuelTypeFilter(option.value)"
                    >
                      <span
                        class="fuel-planner__brand-option"
                        :class="{
                          'fuel-planner__brand-option--selected':
                            isFuelTypeOptionSelected(option.value),
                        }"
                      >
                        <span
                          class="fuel-planner__brand-option-checkbox"
                          aria-hidden="true"
                        >
                          <svg
                            v-if="isFuelTypeOptionSelected(option.value)"
                            class="fuel-planner__brand-option-check"
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                        <span class="fuel-planner__brand-option-label">{{
                          option.label
                        }}</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
          </div>
          <p class="fuel-planner__found-title">{{ foundStationsTitle }}</p>
          <template v-if="plannerMode === 'region'">
            <p
              v-if="!selectedRegionIds.length && !selectedBrandFilters.length"
              class="fuel-planner__hint"
            >
              Выберите регионы и/или бренды выше
            </p>
            <p v-else-if="isLoadingRegion" class="fuel-planner__hint">Ищем АЗС…</p>
            <p v-else-if="!stations.length" class="fuel-planner__hint">
              {{
                selectedRegionIds.length
                  ? 'АЗС в выбранных регионах не найдены'
                  : 'АЗС выбранных брендов не найдены'
              }}
            </p>
            <p v-else-if="!visibleStations.length" class="fuel-planner__hint">
              {{ visibleStationsEmptyHint }}
            </p>
            <ul v-else class="fuel-planner__found-list">
              <li
                v-for="station in visibleStations"
                :key="station.id"
                class="fuel-planner__found-item"
                role="button"
                tabindex="0"
                @click="openStationPopup(station.id)"
                @keydown.enter.prevent="openStationPopup(station.id)"
              >
                <span class="fuel-planner__found-name">{{ station.name }}</span>
                <span class="fuel-planner__found-brand">{{
                  formatStationBrand(station)
                }}</span>
                <span v-if="station.address" class="fuel-planner__found-address">
                  {{ station.address }}
                </span>
                <FuelPriceCards :prices="stationPricesForDisplay(station)" />
              </li>
            </ul>
          </template>
          <template v-else-if="routeBuilt">
            <p v-if="!stations.length" class="fuel-planner__hint">
              АЗС рядом с маршрутом не найдены
            </p>
            <p v-else-if="!visibleStations.length" class="fuel-planner__hint">
              {{ visibleStationsEmptyHint }}
            </p>
            <ul v-else class="fuel-planner__found-list">
              <li
                v-for="station in visibleStations"
                :key="station.id"
                class="fuel-planner__found-item"
                :class="{ 'fuel-planner__found-item--selected': station.selected }"
                role="button"
                tabindex="0"
                @click="openStationPopup(station.id)"
                @keydown.enter.prevent="openStationPopup(station.id)"
              >
                <span class="fuel-planner__found-name">{{ station.name }}</span>
                <span class="fuel-planner__found-brand">{{
                  formatStationBrand(station)
                }}</span>
                <span v-if="station.address" class="fuel-planner__found-address">
                  {{ station.address }}
                </span>
                <FuelPriceCards :prices="stationPricesForDisplay(station)" />
              </li>
            </ul>
          </template>
          <p v-else class="fuel-planner__hint">
            Постройте маршрут, чтобы увидеть АЗС
          </p>
        </div>
      </aside>

      <section
        class="fuel-planner__map-panel"
        :class="{
          'fuel-planner__map-panel--fullscreen': mapFullscreen,
          'fuel-planner__map-panel--region-browse':
            plannerMode === 'region' && !mapFullscreen,
          'fuel-planner__map-panel--form-open':
            mapFullscreen && mapFormVisible,
        }"
      >
        <div v-if="!mapLoadError" class="fuel-planner__map-toolbar">
          <button
            type="button"
            class="fuel-planner__map-tool-btn"
            :title="mapFullscreen ? 'Свернуть карту' : 'Раскрыть карту'"
            :aria-label="mapFullscreen ? 'Свернуть карту' : 'Раскрыть карту'"
            @click="toggleMapFullscreen"
          >
            <svg
              v-if="!mapFullscreen"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 3v3a2 2 0 0 1-2 2H3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M21 8h-3a2 2 0 0 1-2-2V3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3 16h3a2 2 0 0 1 2 2v3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16 21v-3a2 2 0 0 1 2-2h3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            v-if="mapFullscreen"
            type="button"
            class="fuel-planner__map-tool-btn"
            :class="{ 'fuel-planner__map-tool-btn--active': mapFormVisible }"
            :title="mapFormVisible ? 'Скрыть панель' : 'Показать панель'"
            :aria-label="mapFormVisible ? 'Скрыть панель' : 'Показать панель'"
            :aria-pressed="mapFormVisible"
            @click="toggleMapFormVisible"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 6h13M8 12h13M8 18h13"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div
            class="fuel-planner__map-legend"
            title="«Ф» на точке — франшиза"
          >
            <span class="fuel-planner__map-legend-badge" aria-hidden="true"
              >Ф</span
            >
            <span class="fuel-planner__map-legend-text">франшиза</span>
          </div>
        </div>
        <div v-if="mapLoadError" class="fuel-planner__map-fallback">
          {{ mapLoadError }}
        </div>
        <div
          v-show="!mapLoadError"
          ref="mapContainer"
          class="fuel-planner__map"
        />
        <div
          v-show="!mapFullscreen && plannerMode === 'route'"
          class="fuel-planner__bottom-panels"
        >
          <div class="fuel-planner__selected">
            <div class="fuel-planner__selected-header">
              <p class="fuel-planner__selected-title">Заправки в маршруте</p>
              <div v-if="routeBuilt" class="fuel-planner__save-actions">
                <template v-if="activeFavoriteId != null">
                  <button
                    type="button"
                    class="btn-primary fuel-planner__save-btn"
                    :disabled="isBuilding || isSaving"
                    @click="openSaveFavoriteModal()"
                  >
                    {{ isSaving ? 'Сохраняем…' : 'Сохранить как новый' }}
                  </button>
                  <button
                    type="button"
                    class="btn-primary fuel-planner__save-btn"
                    :disabled="isBuilding || isSaving"
                    @click="openSaveFavoriteModal({ update: true })"
                  >
                    {{ isSaving ? 'Сохраняем…' : 'Изменить текущий' }}
                  </button>
                </template>
                <button
                  v-else
                  type="button"
                  class="btn-primary fuel-planner__save-btn"
                  :disabled="isBuilding || isSaving"
                  @click="openSaveFavoriteModal()"
                >
                  {{ isSaving ? 'Сохраняем…' : 'Сохранить маршрут' }}
                </button>
              </div>
            </div>
            <template v-if="routeBuilt">
              <ul
                v-if="selectedStations.length"
                class="fuel-planner__selected-list"
              >
                <li
                  v-for="station in selectedStations"
                  :key="station.id"
                  class="fuel-planner__selected-item"
                >
                  <div class="fuel-planner__selected-main">
                    <span class="fuel-planner__selected-name">{{
                      station.name
                    }}</span>
                    <span class="fuel-planner__selected-brand">{{
                      formatStationBrand(station)
                    }}</span>
                    <FuelPriceCards
                      compact
                      :prices="stationPricesForDisplay(station)"
                    />
                  </div>
                  <button
                    type="button"
                    class="fuel-planner__selected-remove"
                    :disabled="isBuilding"
                    @click="removeStationFromRoute(station.id)"
                  >
                    Убрать
                  </button>
                </li>
              </ul>
            </template>
            <p v-else class="fuel-planner__hint">
              Постройте маршрут, чтобы добавить заправки
            </p>
          </div>

          <div class="fuel-planner__favorites">
            <p class="fuel-planner__favorites-title">Избранные маршруты</p>
            <p v-if="!routePlannerFavorites.length" class="fuel-planner__hint">
              Пока нет сохранённых маршрутов
            </p>
            <ul v-else class="fuel-planner__favorites-list">
              <li
                v-for="fav in routePlannerFavorites"
                :key="fav.id"
                class="fuel-planner__favorites-item"
                :class="{
                  'fuel-planner__favorites-item--selected':
                    activeFavoriteId === fav.id,
                }"
              >
                <button
                  type="button"
                  class="fuel-planner__favorites-apply"
                  :disabled="isBuilding || isSaving"
                  @click="applyFavorite(fav)"
                >
                  <span class="fuel-planner__favorites-name">{{
                    fav.name
                  }}</span>
                  <span
                    class="fuel-planner__favorites-route"
                    :title="favoriteRouteLabel(fav)"
                    >{{ favoriteRouteLabel(fav) }}</span
                  >
                  <span class="fuel-planner__favorites-stations">{{
                    favoriteStationsCountLabel(fav)
                  }}</span>
                </button>
                <div class="fuel-planner__favorites-actions">
                  <button
                    type="button"
                    class="fuel-planner__favorites-delete"
                    :disabled="isDeletingFavorite || isBuilding || isSaving"
                    @click="openDeleteFavoriteModal(fav.id)"
                  >
                    Удалить
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <FuelStationPopup
        v-if="activeStation"
        :station="activeStation"
        :prices="stationPricesForDisplay(activeStation)"
        :route-actions="plannerMode === 'route'"
        @add="addStationFromPopup"
        @remove="removeStationFromPopup"
        @close="closeStationPopup"
      />

      <div
        v-if="favoriteToDeleteId != null"
        class="fuel-planner__confirm-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fuel-planner-delete-favorite-title"
        @click.self="closeDeleteFavoriteModal"
      >
        <div class="fuel-planner__confirm-modal">
          <p
            id="fuel-planner-delete-favorite-title"
            class="fuel-planner__confirm-title"
          >
            Удалить маршрут из избранного?
          </p>
          <div class="fuel-planner__confirm-actions">
            <button
              type="button"
              class="btn-secondary"
              :disabled="isDeletingFavorite"
              @click="closeDeleteFavoriteModal"
            >
              Отмена
            </button>
            <button
              type="button"
              class="btn-primary"
              :disabled="isDeletingFavorite"
              @click="confirmDeleteFavorite"
            >
              Удалить
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="saveFavoriteMode != null"
        class="fuel-planner__confirm-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fuel-planner-save-favorite-title"
        @click.self="closeSaveFavoriteModal"
      >
        <div class="fuel-planner__confirm-modal">
          <p
            id="fuel-planner-save-favorite-title"
            class="fuel-planner__confirm-title"
          >
            {{ saveFavoriteModalTitle }}
          </p>
          <label class="fuel-planner__confirm-field">
            <span class="fuel-planner__confirm-label">Название</span>
            <input
              ref="saveFavoriteNameInput"
              v-model="saveFavoriteName"
              type="text"
              class="fuel-planner__confirm-input"
              maxlength="200"
              :disabled="isSaving"
              @keydown.enter.prevent="confirmSaveFavorite"
            />
          </label>
          <div class="fuel-planner__confirm-actions">
            <button
              type="button"
              class="btn-secondary"
              :disabled="isSaving"
              @click="closeSaveFavoriteModal"
            >
              Отмена
            </button>
            <button
              type="button"
              class="btn-primary"
              :disabled="isSaving"
              @click="confirmSaveFavorite"
            >
              {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { markRaw } from 'vue'
  import { mapState, mapMutations } from 'vuex'
  import maplibregl from 'maplibre-gl'
  import 'maplibre-gl/dist/maplibre-gl.css'
  import FuelStationPopup from '@/components/FuelPlanner/FuelStationPopup.vue'
  import FuelPriceCards from '@/components/FuelPlanner/FuelPriceCards.vue'
  import Loader from '@/components/common/Loader.vue'
  import {
    formatFuelCardPrice,
    formatRubChange
  } from '@/utils/fuelPriceDisplay'
  import {
    loadRegionsMeta,
    loadRegionFeature,
    filterStationsByRegionFeatures,
    geometryBBox,
    unionBBoxes
  } from '@/utils/russiaRegions'
  import {
    toFranchiseBool,
    formatStationBrand
  } from '@/utils/stationBrand'

  const SUGGEST_MIN_CHARS = 2
  const SUGGEST_DEBOUNCE_MS = 400
  const SUGGEST_RESULTS = 7
  const ROUTE_BUILD_TIMEOUT_MS = 25000
  const STATION_CORRIDOR_M = 400
  const STATION_BBOX_MARGIN_M = 500
  const METERS_PER_DEG_LAT = 111320
  const STATIONS_SOURCE_ID = 'fuel-stations'
  const STATIONS_LAYER_ID = 'fuel-stations-circle'
  const STATIONS_FRANCHISE_LAYER_ID = 'fuel-stations-franchise'
  const FRANCHISE_LETTER_IMAGE_ID = 'fuel-franchise-letter'

  /** Только буква «Ф» на прозрачном фоне — кладётся поверх circle бренда */
  function buildFranchiseLetterImageData(size = 32) {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const cx = size / 2
    const cy = size / 2
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = '#ffffff'
    ctx.font = `700 ${Math.round(size * 0.72)}px "Segoe UI", Arial, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('Ф', cx, cy + size * 0.03)
    return ctx.getImageData(0, 0, size, size)
  }
  const REGION_SOURCE_ID = 'fuel-region'
  const REGION_FILL_LAYER_ID = 'fuel-region-fill'
  const REGION_LINE_LAYER_ID = 'fuel-region-line'
  const ROUTE_ALTS_SOURCE_ID = 'route-alts'
  const ROUTE_ALTS_LAYER_ID = 'route-alts-line'
  /** Макс. промежуточных (via) между стартом и конечной; всего ≤ 10 точек */
  const MAX_MID_POINTS = 8
  /** Кириллические метки по индексу: 0→А, 1→Б, … последняя = назначение */
  const POINT_LABELS_CYR = 'АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЭЮЯ'
  /** Префикс address в favorites.waypoints для address-via (не АЗС) */
  const VIA_ADDRESS_PREFIX = 'via:'
  /** Не дублировать RF-corridor via рядом с пользовательской точкой */
  const RF_VIA_NEAR_USER_M = 25000
  /** Яндекс rtext: не плодить соседние vias (mid / АЗС) */
  const YANDEX_VIA_DEDUP_M = 35000
  let _midPointKeySeq = 0

  function pointLabelAt(index) {
    const i = Number(index)
    if (i >= 0 && i < POINT_LABELS_CYR.length) return POINT_LABELS_CYR[i]
    return String(i + 1)
  }

  /** mid[0] → Б, mid[1] → В, … */
  function midPointLabelAt(index) {
    return pointLabelAt(Number(index) + 1)
  }

  /** Конечная точка: после всех mid */
  function destinationLabelAt(midCount) {
    return pointLabelAt(Number(midCount) + 1)
  }

  function isViaFavoriteWaypoint(wp) {
    return String(wp?.address || '')
      .trim()
      .startsWith(VIA_ADDRESS_PREFIX)
  }

  function viaAddressFromFavorite(wp) {
    const addr = String(wp?.address || '').trim()
    if (addr.startsWith(VIA_ADDRESS_PREFIX)) {
      return addr.slice(VIA_ADDRESS_PREFIX.length).trim()
    }
    return addr
  }
  // В dev — через proxy vue.config.js (CORS / User-Agent); в prod — напрямую
  const NOMINATIM_BASE =
    process.env.NODE_ENV === 'development'
      ? '/nominatim'
      : 'https://nominatim.openstreetmap.org'
  const OSRM_BASE =
    process.env.NODE_ENV === 'development'
      ? '/osrm'
      : 'https://router.project-osrm.org'
  // DaData address suggest (русские подписи + coords). Secret на фронт не кладём.
  const DADATA_SUGGEST_URL =
    'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address'
  const DADATA_API_KEY = String(process.env.VUE_APP_DADATA_API_KEY || '').trim()
  // OpenFreeMap (OpenMapTiles) — бесплатно, без ключа; подписи локализуем на ru после load
  const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty'

  // Приблизительный bbox РФ (материк + Чукотка западнее 180°)
  const RF_LAT_MIN = 41
  const RF_LAT_MAX = 82
  const RF_LON_MAIN_MIN = 19
  const RF_LON_MAIN_MAX = 180
  const RF_LON_CHUKOTKA_MIN = -180
  const RF_LON_CHUKOTKA_MAX = -168
  // Доля точек вне РФ (после исключения соседних государств из «ложного» RF bbox)
  const ROUTE_OUTSIDE_RF_RATIO = 0.05
  /** Чуть мягче, когда OSRM гоняется через Transsib / Primorye vias */
  const ROUTE_OUTSIDE_RF_RATIO_CORRIDOR = 0.08
  const ROUTE_RF_SAMPLE_MAX = 48
  // Длинный широтный маршрут → vias по Транссибу внутри РФ
  const RF_CORRIDOR_LON_SPAN_DEG = 35
  const RF_CORRIDOR_MIN_DISTANCE_M = 2500 * 1000
  /**
   * Узкий lon-margin: 0.5° выкидывал Тюмень после mid=Курган
   * (Δlon≈0.21°) → OSRM Курган→Омск через Петропавловск.
   * Дубли у пользовательских точек режет RF_VIA_NEAR_USER_M.
   */
  const RF_CORRIDOR_LON_MARGIN = 0.05
  /** Приморье: порог ниже общего — иначе Иркутск→Владивосток без FE-vias */
  const RF_CORRIDOR_PRIMORYE_LON_SPAN_DEG = 15
  const RF_CORRIDOR_PRIMORYE_MIN_DISTANCE_M = 1500 * 1000
  /**
   * Нога пересекает пояс Петропавловск/Булаево (Курган→Омск и аналоги):
   * без Тюмени public OSRM уходит в KZ даже на короткой дистанции.
   */
  const RF_PETROPAVL_BAND_WEST_LON = 66.5
  const RF_PETROPAVL_BAND_EAST_LON = 72
  /** Синтетические развилки: public OSRM на РФ часто отдаёт 1 route */
  const SYNTH_ALT_MAX_ROUTES = 3
  const SYNTH_ALT_OFFSET_MIN_M = 15 * 1000
  const SYNTH_ALT_OFFSET_MAX_M = 40 * 1000
  const SYNTH_ALT_AB_MIN_M = 40 * 1000
  const SYNTH_ALT_AB_MAX_M = 1200 * 1000
  const SYNTH_ALT_MIN_LONGER_RATIO = 1.03
  const SYNTH_ALT_MAX_LONGER_RATIO = 1.45
  const SYNTH_ALT_MID_SEPARATION_M = 6 * 1000
  /** Транссиб / М-53 / Дальний Восток [lat, lon] — промежуточные vias для OSRM */
  const RF_CORRIDOR_WAYPOINTS = [
    [56.8389, 60.6057], // Екатеринбург
    [55.4444, 65.3167], // Курган (объезд Петропавловска / Булаево)
    [57.1522, 65.5272], // Тюмень
    [54.9885, 73.3242], // Омск
    [55.0084, 82.9357], // Новосибирск
    [56.0184, 92.8672], // Красноярск
    [52.2869, 104.305], // Иркутск
    [52.0515, 113.4712], // Чита
    [53.987, 123.944], // Сковородино
    [50.916, 128.474], // Белогорск
    [48.793, 132.921], // Биробиджан
    [48.4827, 135.0838], // Хабаровск
    [45.47, 133.4], // Лесозаводск (РФ трасса Хабаровск→Уссурийск)
    [43.8029, 131.9458] // Уссурийск (к Приморью / Владивостоку)
  ]
  /** Индексы якорных Far East vias (Хабаровск, Уссурийск) — для Primorye */
  const RF_CORRIDOR_KHABAROVSK_IDX = 11
  const RF_CORRIDOR_USSURIYSK_IDX = 13
  /**
   * Лесозаводск / Уссурийск — только к/от Приморья (Владивосток).
   * Иначе Хабаровск→запад / Москва→Хабаровск тянут OSRM сначала на юг к Влад-у.
   */
  const RF_CORRIDOR_PRIMORYE_BRANCH_START_IDX = 12

  /** [lat, lng] — грубая проверка территории РФ (без точных границ) */
  function isInRussiaBbox(lat, lng) {
    const la = Number(lat)
    const lo = Number(lng)
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return false
    if (la < RF_LAT_MIN || la > RF_LAT_MAX) return false
    if (lo >= RF_LON_MAIN_MIN && lo <= RF_LON_MAIN_MAX) return true
    if (lo >= RF_LON_CHUKOTKA_MIN && lo <= RF_LON_CHUKOTKA_MAX) return true
    return false
  }

  /**
   * Ядро Казахстана внутри грубого RF bbox (Astana/Karaganda/Petropavl-объезд OSRM).
   * Самара (53.2,50.1), Омск (54.99,73.3), Нск (~55°), Оренбург (~51.8,55.1),
   * Бийск / Горно-Алтайск / Чуйский тракт — не KZ.
   * Усть-Каменогорск (49.95,82.63), Семей, Ridder, Зыряновск — остаются KZ.
   */
  function isInKazakhstanCore(lat, lng) {
    const la = Number(lat)
    const lo = Number(lng)
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return false
    // Восток/центр KZ: Астана, Караганда, Павлодар (южнее Омска/Барнаула)
    if (la >= 41 && la < 53.3 && lo > 66 && lo <= 87) {
      // РФ Алтай / Чуйский тракт (Бийск → Горно-Алтайск → Онгудай → Кош-Агач).
      // Не вырезаем Усть-Каменогорск (lon≈82.6) и Ridder (50.34,83.51).
      if (lo >= 84.5 && la >= 49.3) return false
      if (lo >= 83.5 && la >= 51.2) return false
      return true
    }
    // Северный пояс Петропавловск–Булаево–подход к Исилькулю
    if (la >= 53.2 && la <= 55.2 && lo >= 66 && lo <= 73.05) {
      // Омск (54.99, 73.32) и ближайший подход — РФ
      if (la >= 54.85 && lo >= 73.15) return false
      // Широтный РФ Курган→Омск севернее Петропавловска (54.87,69) — не KZ
      if (la >= 55.0 && lo >= 72.0) return false
      return true
    }
    // Дальний запад KZ (Уральск и южнее) — lat < 53, чтобы не задеть Самару (53.2,50.1)
    if (la >= 41 && la < 53.0 && lo >= 48 && lo <= 52) {
      // Поволжье РФ (Ростов→Самара→Оренбург): западнее meridian Уральска (~51.2°E)
      if (lo < 51.2 && la >= 48.5) return false
      // Самарская / Башкирия / подход к Оренбургу с севера (Buzuluk и т.п.)
      if (lo >= 51.2 && la >= 52.0) return false
      return true
    }
    // Центр-запад / пояс Костаная
    if (la >= 41 && la < 53.5 && lo > 52 && lo <= 66) {
      // Южный Урал РФ: Оренбург / Орск / Магнитогорск / Башкирия (M5)
      if (la >= 51.0 && lo >= 52.0 && lo <= 61.5) return false
      return true
    }
    return false
  }

  /**
   * Ядро Маньчжурии — только пояса, куда уходит shortcut OSRM.
   * Не трогаем ЕАО / Амур / подход к Хабаровску (lat 46–51, lon 127–135).
   */
  function isInChinaNortheast(lat, lng) {
    const la = Number(lat)
    const lo = Number(lng)
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return false
    // Харбинский пояс
    if (la >= 43.5 && la <= 48.2 && lo >= 122 && lo <= 129.5) return true
    // Муданьцзян / Суйфэньхэ — lon < 130.8, чтобы не задеть Приморье РФ
    if (la >= 43.2 && la <= 45.5 && lo >= 128.5 && lo < 130.8) return true
    // Цицикар
    if (la >= 46.5 && la <= 48.5 && lo >= 122 && lo <= 126) return true
    return false
  }

  /** Ядро Монголии южнее Транссиба (lat ≥ 50 / Чита–Улан-Удэ не трогаем) */
  function isInMongoliaCore(lat, lng) {
    const la = Number(lat)
    const lo = Number(lng)
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return false
    if (la < 41 || la >= 49.5 || lo < 92 || lo > 116) return false
    return true
  }

  /** Точка в Приморье (Владивосток и юг края) — якоря Хабаровск/Уссурийск */
  function isInPrimoryeDest(lat, lng) {
    const la = Number(lat)
    const lo = Number(lng)
    return Number.isFinite(la) && Number.isFinite(lo) && la < 46 && lo > 130
  }

  /** Грубый RF bbox минус ядра соседних государств (KZ / PRC NE / MN) */
  function isLatLngInRussia(lat, lng) {
    if (!isInRussiaBbox(lat, lng)) return false
    if (isInKazakhstanCore(lat, lng)) return false
    if (isInChinaNortheast(lat, lng)) return false
    if (isInMongoliaCore(lat, lng)) return false
    return true
  }

  /** GeoJSON path [lng, lat][] — доля точек вне РФ */
  function routeOutsideRussiaRatio(lngLats) {
    const path = Array.isArray(lngLats) ? lngLats : []
    if (!path.length) return 1
    const sampleCount = Math.min(ROUTE_RF_SAMPLE_MAX, path.length)
    let outside = 0
    for (let i = 0; i < sampleCount; i++) {
      const idx =
        sampleCount === 1
          ? 0
          : Math.round((i / (sampleCount - 1)) * (path.length - 1))
      const lng = Number(path[idx]?.[0])
      const lat = Number(path[idx]?.[1])
      if (!isLatLngInRussia(lat, lng)) outside += 1
    }
    return outside / sampleCount
  }

  /**
   * KZ — нулевая толерантность: любой сэмпл в isInKazakhstanCore = fail.
   * Плотнее, чем ratio (короткий сегмент Петропавловск/Булаево не должен проскочить).
   */
  function routeHitsKazakhstan(lngLats) {
    const path = Array.isArray(lngLats) ? lngLats : []
    if (!path.length) return false
    const sampleCount = Math.min(Math.max(ROUTE_RF_SAMPLE_MAX * 4, 192), path.length)
    for (let i = 0; i < sampleCount; i++) {
      const idx =
        sampleCount === 1
          ? 0
          : Math.round((i / (sampleCount - 1)) * (path.length - 1))
      const lng = Number(path[idx]?.[0])
      const lat = Number(path[idx]?.[1])
      if (isInKazakhstanCore(lat, lng)) return true
    }
    return false
  }

  /** Нога пересекает lon-разрыв, где OSRM режет через Петропавловск */
  function crossesPetropavlShortcutBand(lonA, lonB) {
    const lo = Math.min(lonA, lonB)
    const hi = Math.max(lonA, lonB)
    return lo < RF_PETROPAVL_BAND_WEST_LON && hi > RF_PETROPAVL_BAND_EAST_LON
  }

  /** Нужны ли авто-vias Транссиба для длинного A→B внутри РФ */
  function needsRfCorridorVias(coordsA, coordsB) {
    const latA = Number(coordsA?.[0])
    const lonA = Number(coordsA?.[1])
    const latB = Number(coordsB?.[0])
    const lonB = Number(coordsB?.[1])
    if (
      !Number.isFinite(latA) ||
      !Number.isFinite(lonA) ||
      !Number.isFinite(latB) ||
      !Number.isFinite(lonB)
    ) {
      return false
    }
    if (!isLatLngInRussia(latA, lonA) || !isLatLngInRussia(latB, lonB)) {
      return false
    }
    const lonSpan = Math.abs(lonB - lonA)
    if (lonSpan > RF_CORRIDOR_LON_SPAN_DEG) return true
    const distM = haversineMeters([lonA, latA], [lonB, latB])
    if (distM > RF_CORRIDOR_MIN_DISTANCE_M) return true
    // Курган→Омск / mid на Кургане: без коридора OSRM через KZ
    if (crossesPetropavlShortcutBand(lonA, lonB)) return true
    // Иркутск→Владивосток и аналоги: lon-span < 35°, но нужен FE-коридор
    if (
      (isInPrimoryeDest(latA, lonA) || isInPrimoryeDest(latB, lonB)) &&
      (lonSpan > RF_CORRIDOR_PRIMORYE_LON_SPAN_DEG ||
        distM > RF_CORRIDOR_PRIMORYE_MIN_DISTANCE_M)
    ) {
      return true
    }
    return false
  }

  /**
   * Промежуточные vias коридора [lat, lon] для A→B.
   * Базовый фильтр — lon между A/B; для Приморья всегда дописываем
   * Хабаровск→Уссурийск (lon Хабаровска 135 > lon Владивостока 131.9).
   * Лесозаводск/Уссурийск НЕ включаем, если ни A ни B не в Приморье —
   * иначе Хабаровск→Челябинск сначала уходит во Владивосток и через КНР/Монголию.
   */
  function rfCorridorViasBetween(coordsA, coordsB) {
    const latA = Number(coordsA[0])
    const lonA = Number(coordsA[1])
    const latB = Number(coordsB[0])
    const lonB = Number(coordsB[1])
    const goingEast = lonB > lonA
    const destPrimorye = isInPrimoryeDest(latB, lonB)
    const originPrimorye = isInPrimoryeDest(latA, lonA)
    const needPrimoryeBranch = destPrimorye || originPrimorye

    const minLon = Math.min(lonA, lonB) + RF_CORRIDOR_LON_MARGIN
    const maxLon = Math.max(lonA, lonB) - RF_CORRIDOR_LON_MARGIN

    const selected = []
    const pushUnique = wp => {
      if (!selected.some(s => s[0] === wp[0] && s[1] === wp[1])) {
        selected.push(wp)
      }
    }

    // Обычный отрезок: lon строго между A и B
    if (minLon < maxLon) {
      for (let i = 0; i < RF_CORRIDOR_WAYPOINTS.length; i++) {
        if (
          !needPrimoryeBranch &&
          i >= RF_CORRIDOR_PRIMORYE_BRANCH_START_IDX
        ) {
          continue
        }
        const wp = RF_CORRIDOR_WAYPOINTS[i]
        const lon = wp[1]
        if (lon > minLon && lon < maxLon) pushUnique(wp)
      }
    }

    // SPb/запад → Владивосток/Приморье: Хабаровск lon > B lon — иначе OSRM через КНР
    if (goingEast && destPrimorye) {
      for (let i = 0; i < RF_CORRIDOR_WAYPOINTS.length; i++) {
        const wp = RF_CORRIDOR_WAYPOINTS[i]
        // Дальний Восток после Читы: Сковородино…Уссурийск
        if (wp[1] > lonA + RF_CORRIDOR_LON_MARGIN && i >= 8) {
          pushUnique(wp)
        }
      }
      pushUnique(RF_CORRIDOR_WAYPOINTS[RF_CORRIDOR_KHABAROVSK_IDX])
      pushUnique(RF_CORRIDOR_WAYPOINTS[RF_CORRIDOR_USSURIYSK_IDX])
    }

    // Владивосток → запад: зеркально — Уссурийск, Хабаровск, затем коридор
    if (!goingEast && originPrimorye) {
      pushUnique(RF_CORRIDOR_WAYPOINTS[RF_CORRIDOR_USSURIYSK_IDX])
      pushUnique(RF_CORRIDOR_WAYPOINTS[RF_CORRIDOR_KHABAROVSK_IDX])
      for (let i = RF_CORRIDOR_WAYPOINTS.length - 1; i >= 0; i--) {
        if (i >= RF_CORRIDOR_PRIMORYE_BRANCH_START_IDX) continue
        const wp = RF_CORRIDOR_WAYPOINTS[i]
        if (
          wp[1] < lonA - RF_CORRIDOR_LON_MARGIN &&
          wp[1] > lonB + RF_CORRIDOR_LON_MARGIN
        ) {
          pushUnique(wp)
        }
      }
    }

    // Порядок хода: по индексу коридора (не по lon — Хабаровск восточнее Уссурийска)
    selected.sort((a, b) => {
      const ia = RF_CORRIDOR_WAYPOINTS.findIndex(
        w => w[0] === a[0] && w[1] === a[1]
      )
      const ib = RF_CORRIDOR_WAYPOINTS.findIndex(
        w => w[0] === b[0] && w[1] === b[1]
      )
      return goingEast ? ia - ib : ib - ia
    })
    return selected
  }

  // Минимальный EN→RU fallback для country (Nominatim geocode fallback)
  const NOMINATIM_EN_RU = {
    Russia: 'Россия',
    'Russian Federation': 'Россия'
  }

  function localizeNominatimPart(part) {
    const s = String(part || '').trim()
    if (!s) return ''
    return NOMINATIM_EN_RU[s] || s
  }

  function nominatimPartKey(part) {
    return String(part || '')
      .trim()
      .toLowerCase()
      .replace(/ё/g, 'е')
  }

  /** Есть хотя бы одна кириллическая буква (мягкий фильтр RU UI) */
  function hasCyrillicLetter(text) {
    return /\p{Script=Cyrillic}/u.test(String(text || ''))
  }

  function hasNominatimNameRu(row) {
    return Boolean(String(row?.namedetails?.['name:ru'] || '').trim())
  }

  function pushUniqueNominatimPart(bits, part) {
    const p = localizeNominatimPart(part)
    if (!p) return
    const key = nominatimPartKey(p)
    if (bits.some(b => nominatimPartKey(b) === key)) return
    bits.push(p)
  }

  function nominatimPrimaryName(row) {
    const nd = row?.namedetails || {}
    return String(nd['name:ru'] || nd.name || row?.name || '').trim()
  }

  /** Formatter для Nominatim geocode fallback перед build */
  function nominatimLabel(row, fallback = '') {
    const primary = localizeNominatimPart(nominatimPrimaryName(row))
    const addr = row?.address
    if (addr && typeof addr === 'object') {
      const localityRaw =
        addr.city ||
        addr.town ||
        addr.village ||
        addr.hamlet ||
        addr.municipality ||
        ''
      const districtRaw =
        addr.county ||
        addr.state_district ||
        addr.city_district ||
        addr.suburb ||
        ''
      const regionRaw = addr.state || addr.region || ''
      const countryRaw =
        addr.country ||
        (String(addr.country_code || '').toLowerCase() === 'ru' ? 'Россия' : '')
      const roadRaw = addr.road || ''
      const house = String(addr.house_number || '').trim()

      const bits = []
      if (roadRaw) {
        const road = localizeNominatimPart(roadRaw)
        pushUniqueNominatimPart(bits, house ? `${road}, ${house}` : road)
      }
      const place = primary || localizeNominatimPart(localityRaw)
      pushUniqueNominatimPart(bits, place)
      if (localityRaw) {
        const locality = localizeNominatimPart(localityRaw)
        if (locality && nominatimPartKey(locality) !== nominatimPartKey(place)) {
          pushUniqueNominatimPart(bits, locality)
        }
      }
      pushUniqueNominatimPart(bits, districtRaw)
      pushUniqueNominatimPart(bits, regionRaw)
      pushUniqueNominatimPart(bits, countryRaw)

      const assembled = bits.join(', ')
      if (assembled) return assembled
    }
    if (primary) return primary

    const display = String(row?.display_name || '').trim()
    if (display) {
      const parts = display
        .split(',')
        .map(p => localizeNominatimPart(p.trim()))
        .filter(Boolean)
      const deduped = []
      parts.forEach(p => pushUniqueNominatimPart(deduped, p))
      const fromDisplay = deduped.join(', ')
      if (fromDisplay) return fromDisplay
    }
    return String(fallback || '').trim()
  }

  function applyMapLabelsRu(map) {
    const layers = map?.getStyle?.()?.layers
    if (!Array.isArray(layers)) return
    for (const layer of layers) {
      if (layer.type !== 'symbol') continue
      const textField = layer.layout?.['text-field']
      if (textField == null) continue
      // щиты/номера дорог — не трогаем
      if (Array.isArray(textField) && textField[0] === 'to-string') continue
      try {
        map.setLayoutProperty(layer.id, 'text-field', [
          'coalesce',
          ['get', 'name:ru'],
          ['get', 'name_ru'],
          ['get', 'name'],
          ['get', 'name:nonlatin'],
          ['get', 'name:latin']
        ])
      } catch (_) {
        /* слой без text-field / read-only */
      }
    }
  }
  const EMPTY_LINE = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: [] }
  }
  const EMPTY_POINTS = {
    type: 'FeatureCollection',
    features: []
  }
  const OSRM_UNAVAILABLE_MSG =
    'Не удалось построить маршрут. Попробуйте ещё раз.'

  /** OSRM distance (m) → «180 км» */
  function formatRouteDistance(meters) {
    const m = Number(meters)
    if (!Number.isFinite(m) || m < 0) return '0 км'
    const km = Math.round(m / 1000)
    return `${km} км`
  }

  /** OSRM duration (s) → «2 ч 15 мин» / «45 мин» */
  function formatRouteDuration(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0))
    const h = Math.floor(total / 3600)
    const min = Math.round((total % 3600) / 60)
    if (h > 0) {
      return min > 0 ? `${h} ч ${min} мин` : `${h} ч`
    }
    return `${min} мин`
  }

  /** Haversine distance in meters between GeoJSON [lng, lat] points */
  function haversineMeters(a, b) {
    const toRad = deg => (deg * Math.PI) / 180
    const lat1 = toRad(a[1])
    const lat2 = toRad(b[1])
    const dLat = lat2 - lat1
    const dLng = toRad(b[0] - a[0])
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
    return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)))
  }

  /** Initial bearing deg from [lat,lon] A → B */
  function bearingDegrees(lat1, lon1, lat2, lon2) {
    const toRad = deg => (deg * Math.PI) / 180
    const φ1 = toRad(lat1)
    const φ2 = toRad(lat2)
    const Δλ = toRad(lon2 - lon1)
    const y = Math.sin(Δλ) * Math.cos(φ2)
    const x =
      Math.cos(φ1) * Math.sin(φ2) -
      Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
  }

  /** Destination [lat, lon] from start by bearing (deg) and distance (m) */
  function destinationLatLng(lat, lon, bearingDeg, distM) {
    const R = 6371000
    const δ = distM / R
    const θ = (bearingDeg * Math.PI) / 180
    const φ1 = (lat * Math.PI) / 180
    const λ1 = (lon * Math.PI) / 180
    const sinφ2 =
      Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ)
    const φ2 = Math.asin(Math.min(1, Math.max(-1, sinφ2)))
    const λ2 =
      λ1 +
      Math.atan2(
        Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
        Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
      )
    return [((φ2 * 180) / Math.PI), ((((λ2 * 180) / Math.PI + 540) % 360) - 180)]
  }

  /**
   * Perp offset vias [lat, lon] L/R around A→B midpoint.
   * Public OSRM rarely returns native alts on RF roads — force forks.
   */
  function syntheticAltOffsetVias(coordsA, coordsB) {
    const latA = Number(coordsA[0])
    const lonA = Number(coordsA[1])
    const latB = Number(coordsB[0])
    const lonB = Number(coordsB[1])
    const abM = haversineMeters([lonA, latA], [lonB, latB])
    if (abM < SYNTH_ALT_AB_MIN_M || abM > SYNTH_ALT_AB_MAX_M) return []
    const midLat = (latA + latB) / 2
    const midLon = (lonA + lonB) / 2
    const bearing = bearingDegrees(latA, lonA, latB, lonB)
    const offsetM = Math.min(
      SYNTH_ALT_OFFSET_MAX_M,
      Math.max(SYNTH_ALT_OFFSET_MIN_M, abM * 0.12)
    )
    const left = destinationLatLng(midLat, midLon, (bearing + 90) % 360, offsetM)
    const right = destinationLatLng(midLat, midLon, (bearing + 270) % 360, offsetM)
    return [left, right].filter(
      ([lat, lon]) => isLatLngInRussia(lat, lon)
    )
  }

  /** Vertex near fraction along path [lng, lat][] */
  function pathPointAtFraction(path, fraction) {
    if (!Array.isArray(path) || path.length < 2) return null
    const f = Math.min(1, Math.max(0, Number(fraction) || 0))
    const idx = Math.round(f * (path.length - 1))
    const p = path[idx]
    if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null
    return p
  }

  /** True if cand is a meaningful fork vs primary (not near-duplicate) */
  function isDistinctRouteAlternative(primary, cand) {
    const pd = Number(primary?.distance) || 0
    const cd = Number(cand?.distance) || 0
    if (pd <= 0 || cd <= 0) return false
    if (cd > pd * SYNTH_ALT_MAX_LONGER_RATIO) return false
    const pPath = primary?.pathLngLat
    const cPath = cand?.pathLngLat
    if (!pPath?.length || !cPath?.length) return false
    let sepSum = 0
    let sepN = 0
    for (const f of [0.35, 0.5, 0.65]) {
      const a = pathPointAtFraction(pPath, f)
      const b = pathPointAtFraction(cPath, f)
      if (!a || !b) continue
      sepSum += haversineMeters(a, b)
      sepN += 1
    }
    if (!sepN) return false
    const sepAvg = sepSum / sepN
    const longerEnough = cd >= pd * SYNTH_ALT_MIN_LONGER_RATIO
    return sepAvg >= SYNTH_ALT_MID_SEPARATION_M && (longerEnough || sepAvg >= SYNTH_ALT_MID_SEPARATION_M * 1.5)
  }

  function pathSegmentLengths(path) {
    const lengths = [0]
    for (let i = 1; i < path.length; i++) {
      lengths.push(lengths[i - 1] + haversineMeters(path[i - 1], path[i]))
    }
    return lengths
  }

  /** Distance along path to nearest vertex for [lat, lng] */
  function distanceAlongPath(path, latLng) {
    if (!path?.length || !latLng) return 0
    const target = [Number(latLng[1]), Number(latLng[0])]
    let bestDist = Infinity
    let bestAlong = 0
    const cum = pathSegmentLengths(path)
    for (let i = 0; i < path.length; i++) {
      const d = haversineMeters(path[i], target)
      if (d < bestDist) {
        bestDist = d
        bestAlong = cum[i]
      }
    }
    return bestAlong
  }

  /** Local equirectangular meters around originLat */
  function toLocalMeters(lng, lat, originLat) {
    const cosLat = Math.cos((originLat * Math.PI) / 180) || 1e-6
    return {
      x: lng * METERS_PER_DEG_LAT * cosLat,
      y: lat * METERS_PER_DEG_LAT
    }
  }

  /** Distance from point to segment (both GeoJSON [lng, lat]) in meters */
  function distancePointToSegmentMeters(p, a, b) {
    const originLat = p[1]
    const P = toLocalMeters(p[0], p[1], originLat)
    const A = toLocalMeters(a[0], a[1], originLat)
    const B = toLocalMeters(b[0], b[1], originLat)
    const abx = B.x - A.x
    const aby = B.y - A.y
    const abLen2 = abx * abx + aby * aby
    let t = 0
    if (abLen2 > 0) {
      t = ((P.x - A.x) * abx + (P.y - A.y) * aby) / abLen2
      t = Math.max(0, Math.min(1, t))
    }
    const dx = P.x - (A.x + abx * t)
    const dy = P.y - (A.y + aby * t)
    return Math.sqrt(dx * dx + dy * dy)
  }

  /** Min distance from [lng, lat] to polyline [[lng,lat],…] in meters */
  function distancePointToPolylineMeters(pointLngLat, path) {
    if (!path?.length) return Infinity
    if (path.length === 1) return haversineMeters(path[0], pointLngLat)
    let min = Infinity
    for (let i = 1; i < path.length; i++) {
      const d = distancePointToSegmentMeters(pointLngLat, path[i - 1], path[i])
      if (d < min) min = d
    }
    return min
  }

  function pathBBoxWithMargin(path, marginMeters) {
    let minLng = Infinity
    let maxLng = -Infinity
    let minLat = Infinity
    let maxLat = -Infinity
    for (const c of path) {
      const lng = Number(c[0])
      const lat = Number(c[1])
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    }
    const midLat = (minLat + maxLat) / 2
    const dLat = marginMeters / METERS_PER_DEG_LAT
    const cosLat = Math.cos((midLat * Math.PI) / 180) || 1e-6
    const dLng = marginMeters / (METERS_PER_DEG_LAT * cosLat)
    return {
      minLng: minLng - dLng,
      maxLng: maxLng + dLng,
      minLat: minLat - dLat,
      maxLat: maxLat + dLat
    }
  }

  function isFiniteCoordPair(lat, lng) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
    if (lat === 0 && lng === 0) return false
    return true
  }

  /** Строковое поле latitude/longitude из API → число или null */
  function parseApiCoord(value) {
    if (value == null || value === '') return null
    const n = Number(String(value).trim())
    return Number.isFinite(n) ? n : null
  }

  function parseRawResponse(rawResponse) {
    if (rawResponse && typeof rawResponse === 'object' && !Array.isArray(rawResponse)) {
      return rawResponse
    }
    if (typeof rawResponse === 'string') {
      const trimmed = rawResponse.trim()
      if (!trimmed) return {}
      try {
        let parsed = JSON.parse(trimmed)
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed.trim())
          } catch (e) {
            return {}
          }
        }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed
        }
      } catch (e) {
        return {}
      }
    }
    return {}
  }

  const FUEL_KEY_ORDER = ['ai92', 'ai95', 'ai98', 'dt', 'dtw', 'gas']

  function fuelPriceSortKey(price) {
    const key = String(price?.fuel_key || '').toLowerCase()
    const idx = FUEL_KEY_ORDER.indexOf(key)
    if (idx >= 0) return idx
    const typeId = Number(price?.source_fuel_type_id)
    if (Number.isFinite(typeId)) return 100 + typeId
    return 1000
  }

  function mapApiPriceChanges(raw) {
    if (!raw || typeof raw !== 'object') return null
    const rub = Number(raw.rub)
    const percent = Number(raw.percent)
    const last_date = String(raw.last_date || '').trim()
    return {
      rub: Number.isFinite(rub) ? rub : null,
      percent: Number.isFinite(percent) ? percent : null,
      last_date: last_date || null
    }
  }

  function mapApiPriceRestrictions(raw) {
    if (!raw || typeof raw !== 'object') return null
    return {
      all: raw.all ?? null,
      mobile: raw.mobile ?? null,
      plastic: raw.plastic ?? null
    }
  }

  function stationPricesList(station, allowedFuelKeys = null) {
    let prices = station?.fuels?.length
      ? station.fuels
      : Array.isArray(station?.prices)
        ? station.prices
        : []
    if (!prices.length) return []

    if (allowedFuelKeys?.size) {
      prices = prices.filter(p => allowedFuelKeys.has(fuelTypeFilterKey(p)))
    }
    return prices
  }

  /** prices[] из API → нормализованный массив (fuels / prices на station) */
  function mapApiStationPrices(item) {
    const raw = Array.isArray(item?.prices) ? item.prices : []
    const normalized = []

    for (const p of raw) {
      if (!p || typeof p !== 'object') continue
      const value = Number(p.value)
      if (!Number.isFinite(value)) continue
      const fuel_label = String(p.fuel_label || p.fuel_key || '').trim()
      if (!fuel_label) continue

      normalized.push({
        source_fuel_type_id: p.source_fuel_type_id,
        fuel_key: String(p.fuel_key || '').trim(),
        fuel_label,
        value,
        currency: String(p.currency || 'RUB').trim() || 'RUB',
        source: p.source,
        confidence: p.confidence,
        updated_at: p.updated_at,
        expires_at: p.expires_at,
        is_estimated: Boolean(p.is_estimated),
        changes: mapApiPriceChanges(p.changes),
        restrictions: mapApiPriceRestrictions(p.restrictions),
        type: fuel_label,
        price: value
      })
    }

    normalized.sort((a, b) => {
      const diff = fuelPriceSortKey(a) - fuelPriceSortKey(b)
      if (diff !== 0) return diff
      return a.fuel_label.localeCompare(b.fuel_label, 'ru')
    })

    return normalized
  }

  /** brand → source → «Без бренда» (как в mapApiStationCoordinate) */
  function resolveStationBrand(item) {
    return (
      String(item?.brand ?? '').trim() ||
      String(item?.source ?? '').trim() ||
      'Без бренда'
    )
  }

  /** Строка похожа на публичный номер АЗС (не brand-only label) */
  function looksLikeStationNumberLabel(value) {
    const s = String(value ?? '').trim()
    if (!s) return false
    if (/АЗС|№/i.test(s)) return true
    // коды вида 02-001, 246/014, чисто цифры
    return /^\d[\d\-/]*$/.test(s)
  }

  /**
   * Публичный номер/метка АЗС из raw_response (не DB id / raw.id).
   * Приоритет: n → number → caption → fuelShortcode → name(номер) → kod.
   */
  function resolveStationNumber(raw) {
    if (!raw || typeof raw !== 'object') return ''

    const n = String(raw.n ?? '').trim()
    if (n) return n

    if (raw.number != null && String(raw.number).trim() !== '') {
      if (typeof raw.number === 'number' && Number.isFinite(raw.number)) {
        return `АЗС № ${raw.number}`
      }
      const numStr = String(raw.number).trim()
      if (/АЗС|№/i.test(numStr)) return numStr
      return `АЗС № ${numStr}`
    }

    const caption = String(raw.caption ?? '').trim()
    if (caption) return caption

    const fuelShortcode = String(raw.fuelShortcode ?? '').trim()
    if (fuelShortcode) return fuelShortcode

    const name = String(raw.name ?? '').trim()
    if (name && looksLikeStationNumberLabel(name)) return name

    if (raw.kod != null && String(raw.kod).trim() !== '') {
      return `АЗС № ${String(raw.kod).trim()}`
    }

    return ''
  }

  /** Имя для списка/попапа/hover: номер из raw, иначе описание, иначе id */
  function resolveStationDisplayName(raw, item) {
    const numberLabel = resolveStationNumber(raw)
    if (numberLabel) return numberLabel

    const descriptive =
      String(raw?.name ?? '').trim() ||
      String(raw?.display_name ?? '').trim() ||
      String(raw?.fuelName ?? '').trim()
    if (descriptive) return descriptive

    const fallbackId = item?.station_id ?? item?.id
    return fallbackId != null ? `АЗС № ${fallbackId}` : 'АЗС'
  }

  /** Закреплённые бренды в корне дропдауна (порядок фиксирован) */
  const PINNED_BRAND_NAMES = [
    'Башнефть',
    'Газпромнефть',
    'Ирбис',
    'Лукойл',
    'Роснефть',
    'ТАИФ',
    'Татнефть'
  ]

  function brandMatchKey(name) {
    return String(name || '')
      .trim()
      .toLowerCase()
  }

  /** Цвета маркеров АЗС по бренду (заливка / обводка). Прочие → default. */
  const BRAND_MARKER_COLORS = {
    [brandMatchKey('Газпромнефть')]: { fill: '#0077C8', stroke: '#004F86' },
    [brandMatchKey('Лукойл')]: { fill: '#E31C23', stroke: '#9B1218' },
    [brandMatchKey('Роснефть')]: { fill: '#F5C518', stroke: '#B8860B' },
    [brandMatchKey('Татнефть')]: { fill: '#8BC34A', stroke: '#558B2F' },
    [brandMatchKey('ТАИФ')]: { fill: '#1B5E20', stroke: '#0D3B12' },
    [brandMatchKey('Башнефть')]: { fill: '#6B2D5C', stroke: '#3D1A35' },
    [brandMatchKey('Ирбис')]: { fill: '#9C27B0', stroke: '#6A1B7A' }
  }
  const DEFAULT_BRAND_MARKER_COLOR = { fill: '#78909C', stroke: '#546E7A' }
  const SELECTED_STATION_MARKER_COLOR = { fill: '#F97F20', stroke: '#c45f12' }

  function resolveBrandMarkerColor(brand) {
    return (
      BRAND_MARKER_COLORS[brandMatchKey(brand)] || DEFAULT_BRAND_MARKER_COLOR
    )
  }

  /** Уникальные бренды из полного ответа station-coordinate */
  function uniqueBrandsFromApiList(apiList) {
    const brands = new Set()
    for (const item of Array.isArray(apiList) ? apiList : []) {
      if (!item || typeof item !== 'object') continue
      brands.add(resolveStationBrand(item))
    }
    return [...brands].sort((a, b) => a.localeCompare(b, 'ru'))
  }

  function fuelTypeFilterKey(price) {
    const key = String(price?.fuel_key || '').trim()
    if (key) return key
    const typeId = Number(price?.source_fuel_type_id)
    if (Number.isFinite(typeId)) return `type:${typeId}`
    return String(price?.fuel_label || '').trim()
  }

  function stationFuelPrices(station) {
    if (station?.fuels?.length) return station.fuels
    return Array.isArray(station?.prices) ? station.prices : []
  }

  function uniqueFuelTypesFromStations(stations) {
    const map = new Map()
    for (const station of Array.isArray(stations) ? stations : []) {
      for (const price of stationFuelPrices(station)) {
        const value = fuelTypeFilterKey(price)
        const label = String(price?.fuel_label || price?.fuel_key || value).trim()
        if (value && label && !map.has(value)) map.set(value, label)
      }
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => {
        const toSortKey = v =>
          v.startsWith('type:')
            ? { source_fuel_type_id: Number(v.slice(5)) }
            : { fuel_key: v }
        const diff =
          fuelPriceSortKey(toSortKey(a.value)) - fuelPriceSortKey(toSortKey(b.value))
        if (diff !== 0) return diff
        return a.label.localeCompare(b.label, 'ru')
      })
  }

  /** Уникальные типы топлива из полного ответа station-coordinate */
  function uniqueFuelTypesFromApiList(apiList) {
    const pseudoStations = []
    for (const item of Array.isArray(apiList) ? apiList : []) {
      if (!item || typeof item !== 'object') continue
      const prices = mapApiStationPrices(item)
      pseudoStations.push({ prices, fuels: prices })
    }
    return uniqueFuelTypesFromStations(pseudoStations)
  }

  /** API station-coordinate → UI station; invalid coords → null */
  function mapApiStationCoordinate(item) {
    if (!item || typeof item !== 'object') return null
    const raw = parseRawResponse(item.raw_response)
    let lat = parseApiCoord(item.latitude)
    let lng = parseApiCoord(item.longitude)
    if (lat == null) lat = parseApiCoord(raw.y)
    if (lng == null) lng = parseApiCoord(raw.x)
    if (!isFiniteCoordPair(lat, lng)) return null

    const id = item.id != null ? item.id : item.station_id
    if (id == null) return null

    const name = resolveStationDisplayName(raw, item)
    const address = String(raw.a || raw.address || raw.fuelAdress || '').trim()
    const brand = resolveStationBrand(item)
    const prices = mapApiStationPrices(item)

    return {
      id,
      station_id: item.station_id != null ? item.station_id : null,
      name,
      brand,
      address,
      prices,
      fuels: prices,
      coords: [lat, lng],
      is_franchise: toFranchiseBool(item.is_franchise),
      selected: false,
      pathDistance: 0
    }
  }

  /**
   * АЗС в коридоре route: bbox (±margin) → точная дистанция до полилинии ≤ corridorM.
   */
  function filterStationsAlongRoute(apiList, pathLngLat, corridorM = STATION_CORRIDOR_M) {
    const path = (Array.isArray(pathLngLat) ? pathLngLat : []).filter(
      c =>
        Array.isArray(c) &&
        c.length >= 2 &&
        Number.isFinite(Number(c[0])) &&
        Number.isFinite(Number(c[1]))
    )
    if (path.length < 2) return []

    const bbox = pathBBoxWithMargin(path, STATION_BBOX_MARGIN_M)
    const result = []

    for (const raw of Array.isArray(apiList) ? apiList : []) {
      const station = mapApiStationCoordinate(raw)
      if (!station) continue
      const [lat, lng] = station.coords
      if (
        lng < bbox.minLng ||
        lng > bbox.maxLng ||
        lat < bbox.minLat ||
        lat > bbox.maxLat
      ) {
        continue
      }
      const dist = distancePointToPolylineMeters([lng, lat], path)
      if (dist > corridorM) continue
      station.pathDistance = distanceAlongPath(path, station.coords)
      result.push(station)
    }

    result.sort((a, b) => a.pathDistance - b.pathDistance)
    return result
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  function emptySuggestState() {
    return { open: false, items: [], timer: null, seq: 0, anchorStyle: null }
  }

  function emptyMidPoint() {
    _midPointKeySeq += 1
    return {
      key: `mid-${_midPointKeySeq}`,
      text: '',
      suggest: emptySuggestState()
    }
  }

  function withTimeout(promise, ms, message) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(message)), ms)
      promise.then(
        value => {
          clearTimeout(timer)
          resolve(value)
        },
        err => {
          clearTimeout(timer)
          reject(err)
        }
      )
    })
  }

  export default {
    name: 'FuelPlanner',
    components: { FuelStationPopup, FuelPriceCards, Loader },
    data() {
      return {
        pointA: '',
        pointB: '',
        /** Via между А и конечной (бывшие назначения при «Добавить точку»): { key, text, suggest } */
        midPoints: [],
        isBuilding: false,
        isLoadingStations: false,
        routeBuilt: false,
        mapLoadError: '',
        mapReady: false,
        isPageReady: false,
        isAlive: true,
        suggestA: emptySuggestState(),
        suggestB: emptySuggestState(),
        // АЗС в коридоре текущего маршрута (reactive — попап / список / selected)
        stations: [],
        selectedStations: [],
        // Все бренды из полного API station-coordinate (не только вдоль маршрута)
        allApiBrands: [],
        allApiFuelTypes: [],
        selectedBrandFilters: [],
        selectedFuelTypeFilters: [],
        brandFilterOpen: false,
        fuelTypeFilterOpen: false,
        smartOilBrandsExpanded: false,
        brandFilterSearch: '',
        fuelTypeFilterSearch: '',
        activeStationId: null,
        isSaving: false,
        isDeletingFavorite: false,
        favoriteToDeleteId: null,
        activeFavoriteId: null,
        /** null | 'create' | 'update' — модалка названия избранного */
        saveFavoriteMode: null,
        saveFavoriteName: '',
        yandexLinkCopied: false,
        /** Варианты OSRM: [{ pathLngLat, distance, duration }] */
        routeAlternatives: [],
        selectedRouteIndex: 0,
        isUpdatingAlternativeStations: false,
        mapFullscreen: false,
        /** Форма слева в режиме раскрытой карты */
        mapFormVisible: false,
        /** 'route' | 'region' */
        plannerMode: 'route',
        regionOptions: [],
        selectedRegionOptions: [],
        isLoadingRegion: false,
        regionStationsLoaded: false
      }
    },
    // MapLibre-инстансы и кэш маршрута НЕ в data(): Vue Proxy ломает GL-карту
    created() {
      this._map = null
      this._resizeObserver = null
      // Последний успешный build: { coordsA, coordsB, midCoords, route, baseRoute, stations }
      this._lastRoute = null
      // A→B geometry без waypoint-ов — для сортировки АЗС вдоль коридора
      this._baseRoute = null
      // Coords из DaData: [lat, lng] — без лишнего geocode при build
      this._coordsA = null
      this._coordsB = null
      // key mid → [lat, lng]
      this._coordsMids = Object.create(null)
      // Кэш station-coordinate/get-station-coordinate-all на сессию страницы
      this._stationCoordinatesCache = null
      this._stationCoordinatesLoadPromise = null
      this._stationsClickBound = null
      this._stationsEnterBound = null
      this._stationsMoveBound = null
      this._stationsLeaveBound = null
      this._routeAltsClickBound = null
      this._routeAltsEnterBound = null
      this._routeAltsLeaveBound = null
      this._stationHoverPopup = null
      this._routeAlternativeStationsCache = []
      this._routeAlternativesSeq = 0
      // После applyFavorite: waypoints для применения после buildRoute
      this._pendingFavoriteApply = null
      // true только вокруг await buildRoute() внутри applyFavorite
      this._buildingFromFavorite = false
      // Текущие Feature регионов (outline)
      this._regionFeatures = []
      this._regionLoadSeq = 0
      this._lastRegionIdsKey = ''
    },
    computed: {
      ...mapState({
        routePlannerFavorites: state => state.references.routePlannerFavorites,
        myUser: state => state.user.myUser
      }),
      hasDadataKey() {
        return Boolean(DADATA_API_KEY)
      },
      canBuildRoute() {
        return this.mapReady && !this.mapLoadError
      },
      canAddMidPoint() {
        return this.midPoints.length < MAX_MID_POINTS
      },
      canRemoveDestination() {
        return this.midPoints.length > 0
      },
      destinationLabel() {
        return destinationLabelAt(this.midPoints.length)
      },
      addMidPointTitle() {
        if (this.canAddMidPoint) return 'Добавить точку'
        return `Не больше ${MAX_MID_POINTS + 2} точек маршрута`
      },
      buildDisabledTitle() {
        if (this.canBuildRoute) return ''
        return this.mapLoadError || 'Карта ещё не готова'
      },
      // routeBuilt сбрасывается при смене А/Б; coords берутся из _lastRoute после build
      canOpenInYandexMaps() {
        if (!this.routeBuilt || this.isBuilding) return false
        const last = this._lastRoute
        return (
          !!last &&
          this.isValidLatLng(last.coordsA) &&
          this.isValidLatLng(last.coordsB)
        )
      },
      activeStation() {
        if (!this.activeStationId && this.activeStationId !== 0) return null
        return this.findStationById(this.activeStationId)
      },
      brandOptions() {
        return this.allApiBrands
      },
      pinnedBrandOptions() {
        const byKey = new Map()
        for (const brand of this.allApiBrands) {
          byKey.set(brandMatchKey(brand), brand)
        }
        const result = []
        for (const name of PINNED_BRAND_NAMES) {
          const found = byKey.get(brandMatchKey(name))
          if (found) result.push(found)
        }
        return result
      },
      smartOilBrandOptions() {
        const pinned = new Set(this.pinnedBrandOptions)
        return this.allApiBrands.filter(brand => !pinned.has(brand))
      },
      brandFilterSearchKey() {
        return brandMatchKey(this.brandFilterSearch)
      },
      filteredPinnedBrandOptions() {
        const q = this.brandFilterSearchKey
        if (!q) return this.pinnedBrandOptions
        return this.pinnedBrandOptions.filter(brand =>
          brandMatchKey(brand).includes(q)
        )
      },
      filteredSmartOilBrandOptions() {
        const q = this.brandFilterSearchKey
        if (!q) return this.smartOilBrandOptions
        return this.smartOilBrandOptions.filter(brand =>
          brandMatchKey(brand).includes(q)
        )
      },
      hasVisibleBrandFilterItems() {
        return (
          this.filteredPinnedBrandOptions.length > 0 ||
          this.filteredSmartOilBrandOptions.length > 0
        )
      },
      isSmartOilBrandGroupExpanded() {
        return (
          this.smartOilBrandsExpanded || Boolean(this.brandFilterSearchKey)
        )
      },
      foundStationsTitle() {
        return this.plannerMode === 'region'
          ? 'АЗС на карте'
          : 'АЗС рядом с маршрутом'
      },
      selectedRegionIds() {
        return (Array.isArray(this.selectedRegionOptions)
          ? this.selectedRegionOptions
          : []
        )
          .map(o => (o?.id != null ? String(o.id) : ''))
          .filter(Boolean)
      },
      showBrandFilter() {
        return this.brandOptions.length > 0
      },
      fuelTypeOptions() {
        if (
          (this.plannerMode === 'route' && this.routeBuilt) ||
          (this.plannerMode === 'region' && this.regionStationsLoaded)
        ) {
          const fromStations = uniqueFuelTypesFromStations(this.stations)
          if (fromStations.length) return fromStations
        }
        return this.allApiFuelTypes
      },
      showFuelTypeFilter() {
        return this.fuelTypeOptions.length > 0
      },
      fuelTypeFilterSearchKey() {
        return brandMatchKey(this.fuelTypeFilterSearch)
      },
      filteredFuelTypeOptions() {
        const q = this.fuelTypeFilterSearchKey
        if (!q) return this.fuelTypeOptions
        return this.fuelTypeOptions.filter(
          option =>
            brandMatchKey(option.label).includes(q) ||
            brandMatchKey(option.value).includes(q)
        )
      },
      hasVisibleFuelTypeFilterItems() {
        return this.filteredFuelTypeOptions.length > 0
      },
      isAllBrandsSelected() {
        return (
          this.brandOptions.length > 0 &&
          this.selectedBrandFilters.length === this.brandOptions.length
        )
      },
      isAllSmartOilBrandsSelected() {
        const smart = this.smartOilBrandOptions
        return (
          smart.length > 0 &&
          smart.every(brand => this.selectedBrandFilters.includes(brand))
        )
      },
      isSmartOilBrandsPartiallySelected() {
        const smart = this.smartOilBrandOptions
        if (!smart.length || this.isAllSmartOilBrandsSelected) return false
        return smart.some(brand => this.selectedBrandFilters.includes(brand))
      },
      isAllFuelTypesSelected() {
        return (
          this.fuelTypeOptions.length > 0 &&
          this.selectedFuelTypeFilters.length === this.fuelTypeOptions.length
        )
      },
      brandFilterLabel() {
        const count = this.selectedBrandFilters.length
        if (!count) return 'Выберите бренды'
        if (count === 1) return this.selectedBrandFilters[0]
        return `Выбрано ${count}`
      },
      showBrandFilterTriggerLabel() {
        return !this.brandFilterOpen && !this.brandFilterSearch
      },
      fuelTypeFilterLabel() {
        const count = this.selectedFuelTypeFilters.length
        if (!count) return 'Выберите типы топлива'
        if (count === 1) {
          const selected = this.selectedFuelTypeFilters[0]
          const option = this.fuelTypeOptions.find(o => o.value === selected)
          return option?.label || selected
        }
        return `Выбрано ${count}`
      },
      showFuelTypeFilterTriggerLabel() {
        return !this.fuelTypeFilterOpen && !this.fuelTypeFilterSearch
      },
      visibleStationsEmptyHint() {
        const hasBrandFilter = this.selectedBrandFilters.length > 0
        const hasFuelFilter =
          this.fuelTypeOptions.length > 0 && this.selectedFuelTypeFilters.length > 0
        if (hasBrandFilter && hasFuelFilter) {
          return 'Нет АЗС по выбранным брендам и типам топлива'
        }
        if (hasBrandFilter) return 'Нет АЗС по выбранным брендам'
        if (hasFuelFilter) return 'Нет АЗС по выбранным типам топлива'
        return 'Нет АЗС по выбранным фильтрам'
      },
      visibleStations() {
        let filtered = this.stations
        if (this.selectedBrandFilters.length) {
          const allowedBrands = new Set(this.selectedBrandFilters)
          filtered = filtered.filter(s => allowedBrands.has(s.brand))
        }
        if (this.fuelTypeOptions.length && this.selectedFuelTypeFilters.length) {
          const allowedFuels = new Set(this.selectedFuelTypeFilters)
          filtered = filtered.filter(s =>
            stationFuelPrices(s).some(p => allowedFuels.has(fuelTypeFilterKey(p)))
          )
        }
        return filtered
      },
      mapStations() {
        const byId = new Map()
        this.visibleStations.forEach(s => byId.set(String(s.id), s))
        if (this.plannerMode === 'route') {
          this.selectedStations.forEach(s => byId.set(String(s.id), s))
        }
        return [...byId.values()]
      },
      saveFavoriteModalTitle() {
        if (this.saveFavoriteMode === 'update') return 'Изменить текущий'
        if (this.activeFavoriteId != null) return 'Сохранить как новый'
        return 'Сохранить в избранное'
      },
      displayFuelTypeFilterKeys() {
        if (!this.fuelTypeOptions.length) return null
        if (!this.selectedFuelTypeFilters.length || this.isAllFuelTypesSelected) {
          return null
        }
        return new Set(this.selectedFuelTypeFilters)
      }
    },
    watch: {
      pointA() {
        this.invalidateBuiltRoute()
      },
      pointB() {
        this.invalidateBuiltRoute()
      },
      // computed mapStations: любой splice/push фильтров → новый массив → sync на карте
      mapStations() {
        this.syncStationsOnMap()
      },
      selectedRegionOptions(options) {
        if (this.plannerMode !== 'region') return
        this.onRegionOptionsChange(options)
      },
      // Без регионов бренд-фильтр задаёт состав stations (не только visibleStations)
      selectedBrandFilters: {
        deep: true,
        handler() {
          if (this.plannerMode !== 'region') return
          if (this.selectedRegionIds.length) return
          this.loadStationsForBrandBrowse()
        }
      }
    },
    mounted() {
      this.isAlive = true
      this.initMap()
      this.initPage()
      document.addEventListener('mousedown', this.closeFilterDropdowns)
      document.addEventListener('keydown', this.onMapFullscreenKeydown)
      this.$nextTick(() => this.bindSuggestPositionListeners())
    },
    beforeUnmount() {
      this.isAlive = false
      this.setMapFullscreen(false)
      this.unbindMapFullscreenViewportListeners()
      document.removeEventListener('mousedown', this.closeFilterDropdowns)
      document.removeEventListener('keydown', this.onMapFullscreenKeydown)
      document.removeEventListener('keydown', this.onDeleteFavoriteKeydown)
      document.removeEventListener('keydown', this.onSaveFavoriteKeydown)
      this.unbindSuggestPositionListeners()
      clearTimeout(this._yandexLinkCopiedTimer)
      this.clearSuggestTimers()
      this.destroyMap()
    },
    methods: {
      ...mapMutations({
        setMenuChromeHidden: 'SET_MENU_CHROME_HIDDEN'
      }),
      formatStationBrand,
      showError(errorMessage, isSuccess = false) {
        this.$store.dispatch('showError', { errorMessage, isSuccess })
      },
      async ensureRegionOptions() {
        if (this.regionOptions.length) return this.regionOptions
        const regions = await loadRegionsMeta()
        if (!this.isAlive) return []
        this.regionOptions = regions
        return regions
      },
      setPlannerMode(mode) {
        const next = mode === 'region' ? 'region' : 'route'
        if (this.plannerMode === next) return
        if (this.isBuilding || this.isLoadingStations || this.isLoadingRegion) {
          return
        }
        this.plannerMode = next
        this.activeStationId = null
        this.hideStationHoverPopup()
        if (next === 'region') {
          this.clearRegionBrowse({ keepSelection: true })
          this.setRouteLayersVisibility(false)
          this.ensureRegionOptions().catch(() => {})
          // бренды в селекте без предварительного выбора региона
          this.ensureStationCoordinatesCache().catch(() => {})
          if (this.selectedRegionIds.length) {
            this.loadStationsForSelectedRegions()
          } else if (this.selectedBrandFilters.length) {
            this.loadStationsForBrandBrowse()
          }
        } else {
          this.clearRegionOutline()
          this.setRouteLayersVisibility(true)
          this.restoreStationsAfterRegionMode()
        }
        this.$nextTick(() => {
          this.resizeMap()
          requestAnimationFrame(() => this.resizeMap())
        })
      },
      regionIdsKey(ids) {
        return (Array.isArray(ids) ? ids : [])
          .map(String)
          .filter(Boolean)
          .sort()
          .join('\0')
      },
      onRegionOptionsChange(options) {
        const list = Array.isArray(options) ? options : []
        const ids = list
          .map(o => (o?.id != null ? String(o.id) : ''))
          .filter(Boolean)
        const nextKey = this.regionIdsKey(ids)
        const prevKey = this._lastRegionIdsKey || ''
        if (!ids.length) {
          this._lastRegionIdsKey = ''
          // mid-load Clear / снятие всех — тоже инвалидируем in-flight await
          if (
            prevKey ||
            this.regionStationsLoaded ||
            this.stations.length ||
            this.isLoadingRegion
          ) {
            // контуры сбрасываем; при активных брендах — режим «только бренды»
            if (this.selectedBrandFilters.length) {
              this.clearRegionOutline()
              this.loadStationsForBrandBrowse()
            } else {
              this.clearRegionBrowse({ keepSelection: false })
            }
          }
          return
        }
        if (
          nextKey === prevKey &&
          (this.isLoadingRegion || this.regionStationsLoaded)
        ) {
          return
        }
        this._lastRegionIdsKey = nextKey
        this.loadStationsForSelectedRegions()
      },
      clearRegionBrowse({ keepSelection = false } = {}) {
        // сбрасывает устаревший loadStationsForSelectedRegions (seq guard)
        ++this._regionLoadSeq
        this.isLoadingRegion = false
        this.regionStationsLoaded = false
        this.stations = []
        this.activeStationId = null
        if (!keepSelection) {
          this.selectedRegionOptions = []
          this._lastRegionIdsKey = ''
        }
        this.clearRegionOutline()
        this.syncStationsOnMap()
      },
      clearRegionOutline() {
        this._regionFeatures = []
        if (!this._map) return
        try {
          const src = this._map.getSource(REGION_SOURCE_ID)
          if (src) src.setData(EMPTY_POINTS)
        } catch (_) {
          /* ignore */
        }
      },
      syncRegionOutline(features) {
        if (!this._map) return
        try {
          const src = this._map.getSource(REGION_SOURCE_ID)
          if (!src) return
          const list = (Array.isArray(features) ? features : []).filter(
            f => f?.geometry
          )
          if (!list.length) {
            src.setData(EMPTY_POINTS)
            return
          }
          src.setData({
            type: 'FeatureCollection',
            features: list.map(feature => ({
              type: 'Feature',
              properties: feature.properties || {},
              geometry: feature.geometry
            }))
          })
        } catch (_) {
          /* ignore */
        }
      },
      fitMapToRegionFeatures(features) {
        if (!this._map) return
        const list = (Array.isArray(features) ? features : []).filter(
          f => f?.geometry
        )
        if (!list.length) return
        const bbox = unionBBoxes(
          list.map(f => geometryBBox(f.geometry, f.bbox))
        )
        if (!bbox || bbox.length < 4) return
        this.fitMapToLngLatBBox(bbox)
      },
      fitMapToStations(stations) {
        if (!this._map) return
        let minLng = Infinity
        let minLat = Infinity
        let maxLng = -Infinity
        let maxLat = -Infinity
        let any = false
        for (const s of Array.isArray(stations) ? stations : []) {
          const lat = Number(s?.coords?.[0])
          const lng = Number(s?.coords?.[1])
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
          any = true
          if (lng < minLng) minLng = lng
          if (lat < minLat) minLat = lat
          if (lng > maxLng) maxLng = lng
          if (lat > maxLat) maxLat = lat
        }
        if (!any) return
        this.fitMapToLngLatBBox([minLng, minLat, maxLng, maxLat])
      },
      fitMapToLngLatBBox(bbox) {
        if (!this._map || !bbox || bbox.length < 4) return
        const [minLng, minLat, maxLng, maxLat] = bbox
        if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return
        try {
          // Антимеридиан (Чукотка): fitBounds с «перевёрнутым» lng — ненадёжен
          if (minLng > maxLng) {
            this._map.easeTo({
              center: [(minLng + maxLng + 360) / 2 - 180, (minLat + maxLat) / 2],
              zoom: 3,
              duration: 600
            })
            return
          }
          this._map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            { padding: 48, duration: 700, maxZoom: 9 }
          )
        } catch (_) {
          /* ignore */
        }
      },
      setRouteLayersVisibility(visible) {
        if (!this._map) return
        const vis = visible ? 'visible' : 'none'
        ;[
          ROUTE_ALTS_LAYER_ID,
          'route-line',
          'route-point-a',
          'route-point-via',
          'route-point-b'
        ].forEach(id => {
          try {
            if (this._map.getLayer(id)) {
              this._map.setLayoutProperty(id, 'visibility', vis)
            }
          } catch (_) {
            /* ignore */
          }
        })
      },
      restoreStationsAfterRegionMode() {
        if (this.routeBuilt && this._lastRoute?.baseRoute) {
          const saved = Array.isArray(this.selectedStations)
            ? this.selectedStations.map(s => ({ ...s }))
            : []
          this.setStationsFromRoute(this._lastRoute.baseRoute, saved)
          return
        }
        this.stations = []
        this.syncStationsOnMap()
      },
      async loadStationsForSelectedRegions() {
        const regionIds = [...this.selectedRegionIds]
        if (!regionIds.length || this.plannerMode !== 'region') return
        const seq = ++this._regionLoadSeq
        this.isLoadingRegion = true
        this.regionStationsLoaded = false
        this.activeStationId = null
        try {
          const [features, apiList] = await Promise.all([
            Promise.all(regionIds.map(id => loadRegionFeature(id))),
            this.ensureStationCoordinatesCache()
          ])
          if (!this.isAlive || seq !== this._regionLoadSeq) return
          const validFeatures = (features || []).filter(f => f?.geometry)
          if (!validFeatures.length) {
            this.showError('Не удалось загрузить границы регионов')
            this.clearRegionBrowse({ keepSelection: true })
            return
          }
          if (validFeatures.length < regionIds.length) {
            this.showError(
              'Часть границ регионов не загрузилась — показаны доступные'
            )
          }
          this._regionFeatures = validFeatures
          this.syncRegionOutline(validFeatures)
          this.fitMapToRegionFeatures(validFeatures)
          const alongRegion = filterStationsByRegionFeatures(
            apiList,
            validFeatures,
            mapApiStationCoordinate
          )
          alongRegion.forEach(s => {
            s.selected = false
          })
          this.stations = alongRegion
          this.regionStationsLoaded = true
          this.syncStationsOnMap()
        } catch (err) {
          if (!this.isAlive || seq !== this._regionLoadSeq) return
          this.showError(
            err?.message || 'Не удалось загрузить АЗС по регионам'
          )
          this.stations = []
          this.regionStationsLoaded = false
          this.syncStationsOnMap()
        } finally {
          if (this.isAlive && seq === this._regionLoadSeq) {
            this.isLoadingRegion = false
          }
        }
      },
      /** Режим «По региону» без субъектов: АЗС выбранных брендов по всей стране (без PIP). */
      async loadStationsForBrandBrowse() {
        if (this.plannerMode !== 'region') return
        if (this.selectedRegionIds.length) return
        const brands = [...this.selectedBrandFilters]
        const seq = ++this._regionLoadSeq
        this.clearRegionOutline()
        this.activeStationId = null
        if (!brands.length) {
          this.isLoadingRegion = false
          this.regionStationsLoaded = false
          this.stations = []
          this.syncStationsOnMap()
          return
        }
        this.isLoadingRegion = true
        this.regionStationsLoaded = false
        try {
          const apiList = await this.ensureStationCoordinatesCache()
          if (!this.isAlive || seq !== this._regionLoadSeq) return
          const allowed = new Set(brands)
          const mapped = []
          for (const item of Array.isArray(apiList) ? apiList : []) {
            const station = mapApiStationCoordinate(item)
            if (!station || !allowed.has(station.brand)) continue
            station.selected = false
            mapped.push(station)
          }
          this.stations = mapped
          this.regionStationsLoaded = true
          this.syncStationsOnMap()
          this.fitMapToStations(mapped)
        } catch (err) {
          if (!this.isAlive || seq !== this._regionLoadSeq) return
          this.showError(
            err?.message || 'Не удалось загрузить АЗС по брендам'
          )
          this.stations = []
          this.regionStationsLoaded = false
          this.syncStationsOnMap()
        } finally {
          if (this.isAlive && seq === this._regionLoadSeq) {
            this.isLoadingRegion = false
          }
        }
      },
      toggleMapFullscreen() {
        this.setMapFullscreen(!this.mapFullscreen)
      },
      toggleMapFormVisible() {
        if (!this.mapFullscreen) return
        this.mapFormVisible = !this.mapFormVisible
        this.$nextTick(() => {
          this.resizeMap()
          requestAnimationFrame(() => this.resizeMap())
        })
      },
      setMapFullscreen(enabled) {
        const next = Boolean(enabled)
        if (this.mapFullscreen === next) {
          if (!next) {
            this.unbindMapFullscreenViewportListeners()
            this.setMenuChromeHidden(false)
            this.mapFormVisible = false
          }
          return
        }
        this.mapFullscreen = next
        this.mapFormVisible = false
        this.setMenuChromeHidden(next)
        if (next) {
          this.bindMapFullscreenViewportListeners()
        } else {
          this.unbindMapFullscreenViewportListeners()
        }
        this.$nextTick(() => {
          this.resizeMap()
          requestAnimationFrame(() => this.resizeMap())
        })
      },
      bindMapFullscreenViewportListeners() {
        if (this._mapFsViewportBound) return
        this._mapFsViewportBound = true
        this._onMapFsViewport = () => {
          if (!this.mapFullscreen) return
          this.resizeMap()
        }
        window.addEventListener('resize', this._onMapFsViewport)
        window.addEventListener('orientationchange', this._onMapFsViewport)
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this._onMapFsViewport)
        }
      },
      unbindMapFullscreenViewportListeners() {
        if (!this._mapFsViewportBound) return
        this._mapFsViewportBound = false
        if (this._onMapFsViewport) {
          window.removeEventListener('resize', this._onMapFsViewport)
          window.removeEventListener('orientationchange', this._onMapFsViewport)
          if (window.visualViewport) {
            window.visualViewport.removeEventListener(
              'resize',
              this._onMapFsViewport
            )
          }
          this._onMapFsViewport = null
        }
      },
      onMapFullscreenKeydown(e) {
        if (e?.key === 'Escape' && this.mapFullscreen) {
          this.setMapFullscreen(false)
        }
      },
      waitForMapInit(timeoutMs = 30000) {
        if (this.mapReady || this.mapLoadError) {
          return Promise.resolve()
        }
        return new Promise(resolve => {
          let done = false
          let unwatch = null
          const finish = () => {
            if (done) return
            done = true
            if (unwatch) unwatch()
            clearTimeout(timer)
            resolve()
          }
          unwatch = this.$watch(
            () => Boolean(this.mapReady || this.mapLoadError),
            ready => {
              if (ready) finish()
            },
            { immediate: true }
          )
          const timer = setTimeout(finish, timeoutMs)
        })
      },
      async initPage() {
        try {
          await Promise.allSettled([
            this.loadRoutePlannerFavorites(),
            this.ensureStationCoordinatesCache().catch(() => {}),
            this.ensureRegionOptions().catch(() => {}),
            this.waitForMapInit()
          ])
        } finally {
          if (this.isAlive) {
            this.isPageReady = true
          }
        }
      },
      stationPricesForDisplay(station) {
        return stationPricesList(station, this.displayFuelTypeFilterKeys)
      },
      suggestState(point) {
        if (point === 'A') return this.suggestA
        if (point === 'B') return this.suggestB
        const mid = this.midPoints.find(m => m.key === point)
        return mid ? mid.suggest : emptySuggestState()
      },
      pointValue(point) {
        if (point === 'A') return this.pointA
        if (point === 'B') return this.pointB
        const mid = this.midPoints.find(m => m.key === point)
        return mid ? mid.text : ''
      },
      setPointValue(point, value) {
        if (point === 'A') {
          this.pointA = value
          return
        }
        if (point === 'B') {
          this.pointB = value
          return
        }
        const mid = this.midPoints.find(m => m.key === point)
        if (mid) mid.text = value
      },
      midPointLabel(index) {
        return midPointLabelAt(index)
      },
      /**
       * Добавить точку в конец: текущая конечная → via, новая пустая конечная снизу.
       */
      addMidPoint() {
        if (!this.canAddMidPoint || this.isBuilding || this.isLoadingStations) {
          return
        }
        const mid = emptyMidPoint()
        mid.text = String(this.pointB || '')
        const coordsB = this.normalizeLatLng(this._coordsB)
        if (coordsB) {
          if (!this._coordsMids) this._coordsMids = Object.create(null)
          this._coordsMids[mid.key] = coordsB
        }
        if (this.suggestB?.timer) {
          clearTimeout(this.suggestB.timer)
          this.suggestB.timer = null
        }
        this.midPoints.push(mid)
        this.pointB = ''
        this._coordsB = null
        this.suggestB = emptySuggestState()
        this.invalidateBuiltRoute()
        this.$nextTick(() => {
          const el = this.$refs.inputB
          if (el && typeof el.focus === 'function') el.focus()
        })
      },
      removeMidPoint(key) {
        if (this.isBuilding || this.isLoadingStations) return
        const idx = this.midPoints.findIndex(m => m.key === key)
        if (idx < 0) return
        const [removed] = this.midPoints.splice(idx, 1)
        if (removed?.suggest?.timer) {
          clearTimeout(removed.suggest.timer)
          removed.suggest.timer = null
        }
        if (this._coordsMids && key in this._coordsMids) {
          delete this._coordsMids[key]
        }
        this.invalidateBuiltRoute()
      },
      /** Удалить конечную: последняя via становится новым назначением (мин. А + конец). */
      removeDestinationPoint() {
        if (this.isBuilding || this.isLoadingStations) return
        if (!this.midPoints.length) return
        const [last] = this.midPoints.splice(this.midPoints.length - 1, 1)
        if (!last) return
        if (last.suggest?.timer) {
          clearTimeout(last.suggest.timer)
          last.suggest.timer = null
        }
        if (this.suggestB?.timer) {
          clearTimeout(this.suggestB.timer)
          this.suggestB.timer = null
        }
        const lastCoords = this.normalizeLatLng(this._coordsMids?.[last.key])
        if (this._coordsMids && last.key in this._coordsMids) {
          delete this._coordsMids[last.key]
        }
        this.pointB = String(last.text || '')
        this._coordsB = lastCoords
        this.suggestB = emptySuggestState()
        this.invalidateBuiltRoute()
      },
      /**
       * Нормализация coords из draft: { lat, lng } | [lat, lng] → [lat, lng] | null.
       * Пустые/missing geo (Number('')|Number(null) → 0) и (0,0) → null.
       */
      normalizeLatLng(coords) {
        if (Array.isArray(coords) && coords.length >= 2) {
          const pair = [Number(coords[0]), Number(coords[1])]
          return this.isValidLatLng(pair) ? pair : null
        }
        if (coords && typeof coords === 'object') {
          const pair = [
            Number(coords.lat),
            Number(coords.lng ?? coords.lon)
          ]
          return this.isValidLatLng(pair) ? pair : null
        }
        return null
      },
      /**
       * GeoJSON LineString coordinates: [[lng, lat], ...]
       */
      normalizeRouteGeometry(route) {
        if (!Array.isArray(route) || !route.length) return null
        const path = route
          .map(c => {
            if (!Array.isArray(c) || c.length < 2) return null
            const lng = Number(c[0])
            const lat = Number(c[1])
            return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null
          })
          .filter(Boolean)
        return path.length ? path : null
      },
      invalidateBuiltRoute() {
        this.routeBuilt = false
        this._lastRoute = null
        this._baseRoute = null
        this.routeAlternatives = []
        this.selectedRouteIndex = 0
        this.isUpdatingAlternativeStations = false
        this._routeAlternativeStationsCache = []
        this._routeAlternativesSeq += 1
        this.clearStations()
      },
      clearStations() {
        this.activeStationId = null
        this.stations = []
        this.selectedStations = []
        this.closeFilterDropdowns()
        this.syncStationsOnMap()
      },
      resetBrandFilters() {
        this.selectedBrandFilters = [...this.brandOptions]
      },
      resetFuelTypeFilters() {
        this.selectedFuelTypeFilters = this.fuelTypeOptions.map(o => o.value)
      },
      isBrandOptionSelected(brand) {
        return this.selectedBrandFilters.includes(brand)
      },
      isFuelTypeOptionSelected(value) {
        return this.selectedFuelTypeFilters.includes(value)
      },
      toggleSmartOilBrandsExpanded() {
        this.smartOilBrandsExpanded = !this.smartOilBrandsExpanded
      },
      focusBrandFilterSearchInput() {
        this.$nextTick(() => {
          const input = this.$refs.brandFilterSearchInput
          if (input && typeof input.focus === 'function') input.focus()
        })
      },
      focusFuelTypeFilterSearchInput() {
        this.$nextTick(() => {
          const input = this.$refs.fuelTypeFilterSearchInput
          if (input && typeof input.focus === 'function') input.focus()
        })
      },
      openBrandFilter() {
        this.brandFilterOpen = true
        this.fuelTypeFilterOpen = false
        this.fuelTypeFilterSearch = ''
        this.focusBrandFilterSearchInput()
      },
      openFuelTypeFilter() {
        this.fuelTypeFilterOpen = true
        this.brandFilterOpen = false
        this.smartOilBrandsExpanded = false
        this.brandFilterSearch = ''
        this.focusFuelTypeFilterSearchInput()
      },
      onBrandFilterTriggerClick() {
        if (!this.brandFilterOpen) {
          this.openBrandFilter()
        } else {
          this.focusBrandFilterSearchInput()
        }
      },
      onFuelTypeFilterTriggerClick() {
        if (!this.fuelTypeFilterOpen) {
          this.openFuelTypeFilter()
        } else {
          this.focusFuelTypeFilterSearchInput()
        }
      },
      onBrandFilterInputFocus() {
        if (!this.brandFilterOpen) {
          this.brandFilterOpen = true
          this.fuelTypeFilterOpen = false
          this.fuelTypeFilterSearch = ''
        }
      },
      onFuelTypeFilterInputFocus() {
        if (!this.fuelTypeFilterOpen) {
          this.fuelTypeFilterOpen = true
          this.brandFilterOpen = false
          this.smartOilBrandsExpanded = false
          this.brandFilterSearch = ''
        }
      },
      closeBrandFilterFromInput() {
        this.brandFilterOpen = false
        this.smartOilBrandsExpanded = false
        this.brandFilterSearch = ''
        this.$refs.brandFilterSearchInput?.blur?.()
      },
      closeFuelTypeFilterFromInput() {
        this.fuelTypeFilterOpen = false
        this.fuelTypeFilterSearch = ''
        this.$refs.fuelTypeFilterSearchInput?.blur?.()
      },
      toggleBrandFilterOpen() {
        if (this.brandFilterOpen) {
          this.brandFilterOpen = false
          this.smartOilBrandsExpanded = false
          this.brandFilterSearch = ''
        } else {
          this.openBrandFilter()
        }
      },
      toggleFuelTypeFilterOpen() {
        if (this.fuelTypeFilterOpen) {
          this.fuelTypeFilterOpen = false
          this.fuelTypeFilterSearch = ''
        } else {
          this.openFuelTypeFilter()
        }
      },
      closeFilterDropdowns(event) {
        const el = event?.target
        const node = el instanceof Element ? el : el?.parentElement
        if (node?.closest?.('.fuel-planner__filter-dropdown')) return
        this.brandFilterOpen = false
        this.fuelTypeFilterOpen = false
        this.smartOilBrandsExpanded = false
        this.brandFilterSearch = ''
        this.fuelTypeFilterSearch = ''
      },
      toggleAllBrandFilters() {
        if (this.isAllBrandsSelected) {
          this.selectedBrandFilters = []
        } else {
          this.resetBrandFilters()
        }
      },
      toggleAllSmartOilBrandFilters() {
        const smart = this.smartOilBrandOptions
        if (!smart.length) return
        if (this.isAllSmartOilBrandsSelected) {
          const remove = new Set(smart)
          this.selectedBrandFilters = this.selectedBrandFilters.filter(
            brand => !remove.has(brand)
          )
        } else {
          const selected = new Set(this.selectedBrandFilters)
          smart.forEach(brand => {
            if (!selected.has(brand)) this.selectedBrandFilters.push(brand)
          })
        }
      },
      toggleAllFuelTypeFilters() {
        if (this.isAllFuelTypesSelected) {
          this.selectedFuelTypeFilters = []
        } else {
          this.resetFuelTypeFilters()
        }
      },
      toggleBrandFilter(brand) {
        const idx = this.selectedBrandFilters.indexOf(brand)
        if (idx >= 0) {
          this.selectedBrandFilters.splice(idx, 1)
        } else {
          this.selectedBrandFilters.push(brand)
        }
      },
      toggleFuelTypeFilter(value) {
        const idx = this.selectedFuelTypeFilters.indexOf(value)
        if (idx >= 0) {
          this.selectedFuelTypeFilters.splice(idx, 1)
        } else {
          this.selectedFuelTypeFilters.push(value)
        }
      },
      clearSuggestTimers() {
        const states = [
          this.suggestA,
          this.suggestB,
          ...this.midPoints.map(m => m.suggest)
        ]
        states.forEach(state => {
          if (state?.timer) {
            clearTimeout(state.timer)
            state.timer = null
          }
        })
      },
      initMap() {
        try {
          if (!this.$refs.mapContainer) return

          // markRaw + вне data(): Proxy не трогает MapLibre
          const map = new maplibregl.Map({
            container: this.$refs.mapContainer,
            style: MAP_STYLE_URL,
            center: [37.573856, 55.751574],
            zoom: 9,
            attributionControl: true
          })
          this._map = markRaw(map)

          map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

          map.on('load', () => {
            if (!this.isAlive || !this._map) return

            applyMapLabelsRu(map)

            map.addSource(ROUTE_ALTS_SOURCE_ID, {
              type: 'geojson',
              data: EMPTY_POINTS
            })
            map.addSource('route-line', {
              type: 'geojson',
              data: EMPTY_LINE
            })
            map.addSource('route-points', {
              type: 'geojson',
              data: EMPTY_POINTS
            })
            map.addSource(STATIONS_SOURCE_ID, {
              type: 'geojson',
              data: EMPTY_POINTS
            })
            map.addSource(REGION_SOURCE_ID, {
              type: 'geojson',
              data: EMPTY_POINTS
            })

            map.addLayer({
              id: ROUTE_ALTS_LAYER_ID,
              type: 'line',
              source: ROUTE_ALTS_SOURCE_ID,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#9ca3af',
                'line-width': 3,
                'line-opacity': 0.7
              }
            })

            map.addLayer({
              id: 'route-line',
              type: 'line',
              source: 'route-line',
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#6DB534',
                'line-width': 5,
                'line-opacity': 0.9
              }
            })

            map.addLayer({
              id: REGION_FILL_LAYER_ID,
              type: 'fill',
              source: REGION_SOURCE_ID,
              paint: {
                'fill-color': '#6DB534',
                'fill-opacity': 0.08
              }
            })
            map.addLayer({
              id: REGION_LINE_LAYER_ID,
              type: 'line',
              source: REGION_SOURCE_ID,
              paint: {
                'line-color': '#2d6a4f',
                'line-width': 2,
                'line-opacity': 0.85
              }
            })

            // Все АЗС — тот же circle (цвет бренда, тот же размер); у франшизы сверху маленькая «Ф»
            map.addLayer({
              id: STATIONS_LAYER_ID,
              type: 'circle',
              source: STATIONS_SOURCE_ID,
              paint: {
                'circle-radius': [
                  'case',
                  ['==', ['get', 'selected'], 1],
                  9,
                  7
                ],
                'circle-color': [
                  'case',
                  ['==', ['get', 'selected'], 1],
                  SELECTED_STATION_MARKER_COLOR.fill,
                  ['get', 'markerColor']
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': [
                  'case',
                  ['==', ['get', 'selected'], 1],
                  SELECTED_STATION_MARKER_COLOR.stroke,
                  ['get', 'markerStroke']
                ]
              }
            })

            map.addLayer({
              id: 'route-point-a',
              type: 'circle',
              source: 'route-points',
              filter: ['==', ['get', 'label'], 'A'],
              paint: {
                'circle-radius': 8,
                'circle-color': '#6DB534',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#2d6a4f'
              }
            })

            map.addLayer({
              id: 'route-point-via',
              type: 'circle',
              source: 'route-points',
              filter: [
                'all',
                ['!=', ['get', 'label'], 'A'],
                ['!=', ['get', 'label'], 'B']
              ],
              paint: {
                'circle-radius': 7,
                'circle-color': '#e9a23b',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#9a6b1f'
              }
            })

            map.addLayer({
              id: 'route-point-b',
              type: 'circle',
              source: 'route-points',
              filter: ['==', ['get', 'label'], 'B'],
              paint: {
                'circle-radius': 8,
                'circle-color': '#457b9d',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#1d3557'
              }
            })

            try {
              if (!map.hasImage(FRANCHISE_LETTER_IMAGE_ID)) {
                map.addImage(
                  FRANCHISE_LETTER_IMAGE_ID,
                  buildFranchiseLetterImageData(32)
                )
              }
              map.addLayer({
                id: STATIONS_FRANCHISE_LAYER_ID,
                type: 'symbol',
                source: STATIONS_SOURCE_ID,
                filter: ['==', ['get', 'isFranchise'], 1],
                layout: {
                  'icon-image': FRANCHISE_LETTER_IMAGE_ID,
                  // 32px * 0.42 ≈ 13px — почти во весь circle radius 7
                  'icon-size': 0.42,
                  'icon-anchor': 'center',
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true
                }
              })
            } catch (_) {
              /* буква Ф необязательна */
            }

            this.bindStationMapEvents(map)

            this.mapReady = true
            this.$nextTick(() => {
              this.resizeMap()
              this.syncStationsOnMap()
            })
          })

          map.on('error', e => {
            if (!this.isAlive) return
            const msg = e?.error?.message || e?.message || ''
            // Не прячем карту из‑за шрифтов/glyphs (бывший badge «Ф»)
            if (/glyph|font|pbf/i.test(String(msg))) return
            if (msg && !this.mapReady) {
              this.mapLoadError = msg
            }
          })

          if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this.resizeMap())
            this._resizeObserver.observe(this.$refs.mapContainer)
          }

          this.$nextTick(() => this.resizeMap())
        } catch (e) {
          if (!this.isAlive) return
          this.mapReady = false
          this.mapLoadError = e?.message || 'Ошибка инициализации карты.'
        }
      },
      resizeMap() {
        if (this.isAlive && this._map) {
          try {
            this._map.resize()
          } catch (_) {
            /* ignore */
          }
        }
      },
      destroyMap() {
        if (this._resizeObserver) {
          try {
            this._resizeObserver.disconnect()
          } catch (_) {
            /* ignore */
          }
          this._resizeObserver = null
        }
        this.unbindStationMapEvents()
        this.hideStationHoverPopup()
        this._stationHoverPopup = null
        this.removeRouteFromMap()
        if (this._map) {
          this._map.remove()
          this._map = null
        }
        this.mapReady = false
      },
      bindStationMapEvents(map) {
        this.unbindStationMapEvents()
        this._stationsClickBound = e => {
          const feature = e?.features?.[0]
          const id = feature?.properties?.id
          if (id) this.openStationPopup(String(id))
        }
        this._stationsEnterBound = e => {
          if (this._map) this._map.getCanvas().style.cursor = 'pointer'
          this.showStationHoverPopup(e)
        }
        this._stationsMoveBound = e => {
          this.showStationHoverPopup(e)
        }
        this._stationsLeaveBound = () => {
          if (this._map) this._map.getCanvas().style.cursor = ''
          this.hideStationHoverPopup()
        }
        this._routeAltsClickBound = e => {
          const feature = e?.features?.[0]
          const idx = Number(feature?.properties?.routeIndex)
          if (!Number.isFinite(idx)) return
          this.selectRouteAlternative(idx)
        }
        this._routeAltsEnterBound = () => {
          if (this._map) this._map.getCanvas().style.cursor = 'pointer'
        }
        this._routeAltsLeaveBound = () => {
          if (this._map) this._map.getCanvas().style.cursor = ''
        }
        map.on('click', STATIONS_LAYER_ID, this._stationsClickBound)
        map.on('mouseenter', STATIONS_LAYER_ID, this._stationsEnterBound)
        map.on('mousemove', STATIONS_LAYER_ID, this._stationsMoveBound)
        map.on('mouseleave', STATIONS_LAYER_ID, this._stationsLeaveBound)
        if (map.getLayer(STATIONS_FRANCHISE_LAYER_ID)) {
          map.on('click', STATIONS_FRANCHISE_LAYER_ID, this._stationsClickBound)
          map.on(
            'mouseenter',
            STATIONS_FRANCHISE_LAYER_ID,
            this._stationsEnterBound
          )
          map.on(
            'mousemove',
            STATIONS_FRANCHISE_LAYER_ID,
            this._stationsMoveBound
          )
          map.on(
            'mouseleave',
            STATIONS_FRANCHISE_LAYER_ID,
            this._stationsLeaveBound
          )
        }
        map.on('click', ROUTE_ALTS_LAYER_ID, this._routeAltsClickBound)
        map.on('mouseenter', ROUTE_ALTS_LAYER_ID, this._routeAltsEnterBound)
        map.on('mouseleave', ROUTE_ALTS_LAYER_ID, this._routeAltsLeaveBound)
      },
      unbindStationMapEvents() {
        if (!this._map) {
          this._stationsClickBound = null
          this._stationsEnterBound = null
          this._stationsMoveBound = null
          this._stationsLeaveBound = null
          this._routeAltsClickBound = null
          this._routeAltsEnterBound = null
          this._routeAltsLeaveBound = null
          return
        }
        try {
          if (this._stationsClickBound) {
            this._map.off('click', STATIONS_LAYER_ID, this._stationsClickBound)
            this._map.off(
              'click',
              STATIONS_FRANCHISE_LAYER_ID,
              this._stationsClickBound
            )
          }
          if (this._stationsEnterBound) {
            this._map.off(
              'mouseenter',
              STATIONS_LAYER_ID,
              this._stationsEnterBound
            )
            this._map.off(
              'mouseenter',
              STATIONS_FRANCHISE_LAYER_ID,
              this._stationsEnterBound
            )
          }
          if (this._stationsMoveBound) {
            this._map.off(
              'mousemove',
              STATIONS_LAYER_ID,
              this._stationsMoveBound
            )
            this._map.off(
              'mousemove',
              STATIONS_FRANCHISE_LAYER_ID,
              this._stationsMoveBound
            )
          }
          if (this._stationsLeaveBound) {
            this._map.off(
              'mouseleave',
              STATIONS_LAYER_ID,
              this._stationsLeaveBound
            )
            this._map.off(
              'mouseleave',
              STATIONS_FRANCHISE_LAYER_ID,
              this._stationsLeaveBound
            )
          }
          if (this._routeAltsClickBound) {
            this._map.off('click', ROUTE_ALTS_LAYER_ID, this._routeAltsClickBound)
          }
          if (this._routeAltsEnterBound) {
            this._map.off(
              'mouseenter',
              ROUTE_ALTS_LAYER_ID,
              this._routeAltsEnterBound
            )
          }
          if (this._routeAltsLeaveBound) {
            this._map.off(
              'mouseleave',
              ROUTE_ALTS_LAYER_ID,
              this._routeAltsLeaveBound
            )
          }
        } catch (_) {
          /* ignore */
        }
        this._stationsClickBound = null
        this._stationsEnterBound = null
        this._stationsMoveBound = null
        this._stationsLeaveBound = null
        this._routeAltsClickBound = null
        this._routeAltsEnterBound = null
        this._routeAltsLeaveBound = null
      },
      ensureStationHoverPopup() {
        if (this._stationHoverPopup) return this._stationHoverPopup
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 14,
          className: 'fuel-planner-map-popup-wrap',
          maxWidth: '260px'
        })
        this._stationHoverPopup = markRaw(popup)
        return popup
      },
      buildStationHoverPopupHtml(station) {
        const name = escapeHtml(station?.name || 'АЗС')
        const brand = escapeHtml(formatStationBrand(station))
        const address = escapeHtml(station?.address || '')
        const prices = this.stationPricesForDisplay(station)
        let pricesHtml = ''
        if (prices.length) {
          const rows = prices
            .slice(0, 6)
            .map(p => {
              const label = escapeHtml(
                p.fuel_label || p.type || p.fuel_key || ''
              )
              const est = p.is_estimated ? '~' : ''
              const price = formatFuelCardPrice(p.value ?? p.price)
              const change = formatRubChange(p.changes)
              const changeHtml = change
                ? `<span class="fuel-planner-map-popup__change">${escapeHtml(change)}</span>`
                : ''
              return `<div class="fuel-planner-map-popup__price-row"><span class="fuel-planner-map-popup__fuel">${label}</span><span class="fuel-planner-map-popup__price">${est}${price}</span>${changeHtml}</div>`
            })
            .join('')
          pricesHtml = `<div class="fuel-planner-map-popup__prices">${rows}</div>`
        } else {
          pricesHtml =
            '<div class="fuel-planner-map-popup__no-fuels">нет данных</div>'
        }
        return `<div class="fuel-planner-map-popup"><div class="fuel-planner-map-popup__name">${name}</div>${
          brand
            ? `<div class="fuel-planner-map-popup__brand">${brand}</div>`
            : ''
        }${
          address
            ? `<div class="fuel-planner-map-popup__address">${address}</div>`
            : ''
        }${pricesHtml}</div>`
      },
      showStationHoverPopup(e) {
        if (!this._map) return
        const feature = e?.features?.[0]
        if (!feature) return
        const props = feature.properties || {}
        const id = props.id
        const station =
          this.findStationById(id) ||
          ({
            id,
            name: props.name,
            brand: props.brand,
            address: props.address,
            is_franchise: toFranchiseBool(props.isFranchise),
            fuels: [],
            prices: []
          })
        const coords = feature.geometry?.coordinates
        if (!Array.isArray(coords) || coords.length < 2) return
        const popup = this.ensureStationHoverPopup()
        popup
          .setLngLat(coords)
          .setHTML(this.buildStationHoverPopupHtml(station))
          .addTo(this._map)
      },
      hideStationHoverPopup() {
        if (!this._stationHoverPopup) return
        try {
          this._stationHoverPopup.remove()
        } catch (_) {
          /* ignore */
        }
      },
      onPointInput(point) {
        this.invalidateBuiltRoute()
        this.setPointCoords(point, null)
        this.scheduleSuggest(point)
      },
      onPointFocus(point) {
        if (!this.hasDadataKey) return
        const state = this.suggestState(point)
        if (state.items.length) {
          this.positionSuggestDropdown(point)
          state.open = true
        }
      },
      onPointBlur(point) {
        const state = this.suggestState(point)
        setTimeout(() => {
          if (this.isAlive) {
            state.open = false
            state.anchorStyle = null
          }
        }, 150)
      },
      getPointInputEl(point) {
        if (point === 'A') return this.$refs.inputA || null
        if (point === 'B') return this.$refs.inputB || null
        const root = this.$refs.pointsScroll
        if (!root || point == null) return null
        return root.querySelector(
          `[data-suggest-point="${CSS.escape(String(point))}"]`
        )
      },
      positionSuggestDropdown(point) {
        const state = this.suggestState(point)
        const el = this.getPointInputEl(point)
        if (!el) {
          state.anchorStyle = null
          return
        }
        const rect = el.getBoundingClientRect()
        state.anchorStyle = {
          top: `${Math.round(rect.bottom + 2)}px`,
          left: `${Math.round(rect.left)}px`,
          width: `${Math.round(rect.width)}px`
        }
      },
      repositionOpenSuggests() {
        const points = [
          'A',
          'B',
          ...this.midPoints.map(m => m.key)
        ]
        points.forEach(point => {
          const state = this.suggestState(point)
          if (state?.open && state.items?.length) {
            this.positionSuggestDropdown(point)
          }
        })
      },
      bindSuggestPositionListeners() {
        if (this._suggestPosBound) return
        this._suggestPosBound = true
        this._onSuggestReposition = () => this.repositionOpenSuggests()
        window.addEventListener('resize', this._onSuggestReposition)
        window.addEventListener('scroll', this._onSuggestReposition, true)
        this._suggestPointsEl = this.$refs.pointsScroll || null
        if (this._suggestPointsEl) {
          this._suggestPointsEl.addEventListener(
            'scroll',
            this._onSuggestReposition
          )
        }
      },
      unbindSuggestPositionListeners() {
        if (!this._suggestPosBound) return
        this._suggestPosBound = false
        if (this._onSuggestReposition) {
          window.removeEventListener('resize', this._onSuggestReposition)
          window.removeEventListener('scroll', this._onSuggestReposition, true)
          if (this._suggestPointsEl) {
            this._suggestPointsEl.removeEventListener(
              'scroll',
              this._onSuggestReposition
            )
            this._suggestPointsEl = null
          }
          this._onSuggestReposition = null
        }
      },
      scheduleSuggest(point) {
        const state = this.suggestState(point)
        if (state.timer) clearTimeout(state.timer)

        if (!this.hasDadataKey) {
          state.items = []
          state.open = false
          state.anchorStyle = null
          return
        }

        const query = String(this.pointValue(point) || '').trim()
        if (query.length < SUGGEST_MIN_CHARS) {
          state.items = []
          state.open = false
          state.anchorStyle = null
          return
        }

        const seq = ++state.seq
        state.timer = setTimeout(() => {
          this.fetchSuggest(point, query, seq)
        }, SUGGEST_DEBOUNCE_MS)
      },
      async fetchSuggest(point, query, seq) {
        const state = this.suggestState(point)
        if (!this.isAlive || !this.hasDadataKey) return

        try {
          const items = await this.dadataSuggest(query, SUGGEST_RESULTS)
          if (!this.isAlive || state.seq !== seq) return
          state.items = items
          if (state.items.length > 0) {
            this.positionSuggestDropdown(point)
            state.open = true
          } else {
            state.open = false
            state.anchorStyle = null
          }
        } catch (_) {
          if (!this.isAlive || state.seq !== seq) return
          state.items = []
          state.open = false
          state.anchorStyle = null
        }
      },
      applySuggest(point, item) {
        const text = (item?.value || item?.displayName || '').trim()
        if (!text) return
        this.setPointValue(point, text)
        this.setPointCoords(point, item?.coords || null)
        const state = this.suggestState(point)
        state.open = false
        state.anchorStyle = null
        state.items = []
        state.seq += 1
        this.invalidateBuiltRoute()
      },
      setPointCoords(point, coords) {
        const normalized = this.normalizeLatLng(coords)
        if (point === 'A') {
          this._coordsA = normalized
          return
        }
        if (point === 'B') {
          this._coordsB = normalized
          return
        }
        if (!this._coordsMids) this._coordsMids = Object.create(null)
        if (normalized) this._coordsMids[point] = normalized
        else delete this._coordsMids[point]
      },
      pointCoords(point) {
        if (point === 'A') return this._coordsA
        if (point === 'B') return this._coordsB
        return this._coordsMids?.[point] || null
      },
      currentMidCoordsList() {
        return this.midPoints
          .map(m => this.normalizeLatLng(this._coordsMids?.[m.key]))
          .filter(c => this.isValidLatLng(c))
      },
      lastRouteMidCoords() {
        const fromLast = this._lastRoute?.midCoords
        if (Array.isArray(fromLast) && fromLast.length) {
          return fromLast.filter(c => this.isValidLatLng(c))
        }
        return this.currentMidCoordsList()
      },
      isNearAnyLatLng(coords, points, radiusM) {
        if (!this.isValidLatLng(coords)) return false
        const a = this.toLngLat(coords)
        return (Array.isArray(points) ? points : []).some(p => {
          if (!this.isValidLatLng(p)) return false
          return haversineMeters(a, this.toLngLat(p)) <= radiusM
        })
      },
      toApiViaPoint(coords, address) {
        const addr = String(address || '').trim()
        return this.toApiGeoPoint(
          coords,
          addr ? `${VIA_ADDRESS_PREFIX}${addr}` : VIA_ADDRESS_PREFIX
        )
      },
      replaceMidPointsFromFavoriteVias(viaWaypoints) {
        this.clearSuggestTimers()
        const list = Array.isArray(viaWaypoints) ? viaWaypoints : []
        this.midPoints = list.slice(0, MAX_MID_POINTS).map(wp => {
          const mid = emptyMidPoint()
          const coords = this.normalizeLatLng(wp)
          if (coords) this.setPointCoords(mid.key, coords)
          const text = viaAddressFromFavorite(wp)
          if (text) mid.text = text
          else if (coords) {
            mid.text = `${Number(coords[0]).toFixed(5)}, ${Number(coords[1]).toFixed(5)}`
          }
          return mid
        })
        // Drop stale mid coords for removed keys
        const keep = new Set(this.midPoints.map(m => m.key))
        Object.keys(this._coordsMids || {}).forEach(key => {
          if (!keep.has(key)) delete this._coordsMids[key]
        })
      },
      async httpJson(url, options = {}) {
        let res
        try {
          res = await fetch(url, options)
        } catch (e) {
          const err = new Error(
            e?.message || 'Сеть недоступна или запрос заблокирован (CORS/расширения).'
          )
          err.status = 0
          throw err
        }
        if (!res.ok) {
          const err = new Error(`HTTP ${res.status}`)
          err.status = res.status
          throw err
        }
        return res.json()
      },
      nominatimHeaders() {
        return {
          Accept: 'application/json',
          'Accept-Language': 'ru'
        }
      },
      /**
       * DaData suggest/address → русские подписи + geo_lat/geo_lon.
       * Только API-ключ (Token); secret на фронт не отправляем.
       */
      async dadataSuggest(query, limit = SUGGEST_RESULTS) {
        if (!DADATA_API_KEY) return []
        const data = await this.httpJson(DADATA_SUGGEST_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Token ${DADATA_API_KEY}`
          },
          body: JSON.stringify({
            query,
            // Берём с запасом, затем поднимаем пункты с geo в топ
            count: Math.min(Math.max(limit * 2, limit), 20),
            language: 'ru',
            // Только адреса в РФ
            locations: [{ country_iso_code: 'RU' }]
          })
        })
        const list = Array.isArray(data?.suggestions) ? data.suggestions : []
        return list
          .map(row => {
            const text = String(
              row?.value || row?.unrestricted_value || ''
            ).trim()
            const countryIso = String(
              row?.data?.country_iso_code || ''
            ).toUpperCase()
            // DaData без geo отдаёт null/'' → Number → 0; не пишем [0,0]
            const pair = [
              Number(row?.data?.geo_lat),
              Number(row?.data?.geo_lon)
            ]
            const coords = this.isValidLatLng(pair) ? pair : null
            return {
              value: text,
              displayName: text,
              coords,
              countryIso
            }
          })
          .filter(it => {
            if (!it.value) return false
            // Отсекаем явный non-RU и coords вне приблизительного bbox РФ
            if (it.countryIso && it.countryIso !== 'RU') return false
            if (it.coords && !isLatLngInRussia(it.coords[0], it.coords[1])) {
              return false
            }
            return true
          })
          // Пункты с координатами выше (города); регионы без geo — ниже
          .sort((a, b) => Number(!a.coords) - Number(!b.coords))
          .slice(0, limit)
          .map(({ value, displayName, coords }) => ({
            value,
            displayName,
            coords
          }))
      },
      async nominatimSearch(query, limit = 1) {
        const fetchLimit = Math.min(Math.max(limit * 3, limit), 20)
        const params = new URLSearchParams({
          q: query,
          format: 'json',
          addressdetails: '1',
          namedetails: '1',
          'accept-language': 'ru',
          countrycodes: 'ru',
          limit: String(fetchLimit)
        })
        const data = await this.httpJson(`${NOMINATIM_BASE}/search?${params.toString()}`, {
          headers: this.nominatimHeaders()
        })
        const list = Array.isArray(data) ? data : []
        return list
          .map(row => {
            const pair = [Number(row.lat), Number(row.lon)]
            const text = nominatimLabel(row, query)
            const nameRu = hasNominatimNameRu(row)
            const cc = String(row?.address?.country_code || '').toUpperCase()
            return {
              value: text,
              displayName: text,
              coords: this.isValidLatLng(pair) ? pair : null,
              nameRu,
              cc,
              hasCyr: hasCyrillicLetter(text)
            }
          })
          .filter(it => {
            if (!it.value || !it.coords) return false
            // countrycodes=ru + запасной фильтр (и bbox)
            if (it.cc && it.cc !== 'RU') return false
            return isLatLngInRussia(it.coords[0], it.coords[1])
          })
          .sort((a, b) => {
            const aRu = a.cc === 'RU' ? 0 : 1
            const bRu = b.cc === 'RU' ? 0 : 1
            if (aRu !== bRu) return aRu - bRu
            if (a.nameRu !== b.nameRu) return a.nameRu ? -1 : 1
            if (a.hasCyr !== b.hasCyr) return a.hasCyr ? -1 : 1
            return 0
          })
          .slice(0, limit)
          .map(({ value, displayName, coords }) => ({ value, displayName, coords }))
      },
      async geocodePoint(address, label) {
        const list = await this.nominatimSearch(address, 1)
        const first = list[0]
        if (!this.isValidLatLng(first?.coords)) {
          throw new Error(`Не удалось определить координаты ${label}`)
        }
        return { coords: first.coords, text: first.value }
      },
      /**
       * Coords из DaData (или draft) → сразу OSRM; иначе Nominatim search.
       * Missing / (0,0) не считаются валидными — уходим в geocode.
       */
      async resolvePointForBuild(point, address, label) {
        const stored = this.normalizeLatLng(this.pointCoords(point))
        if (stored) {
          this.setPointCoords(point, stored)
          return { coords: stored, text: address }
        }
        this.setPointCoords(point, null)
        return this.geocodePoint(address, label)
      },
      /** Точка [lat, lng] должна быть на территории РФ (bbox − KZ/PRC/MN) */
      assertPointInRussia(coords, pointLabel) {
        if (!this.isValidLatLng(coords)) {
          throw new Error(`Не удалось определить координаты ${pointLabel}`)
        }
        if (!isLatLngInRussia(coords[0], coords[1])) {
          throw new Error(
            `Точка ${pointLabel} должна быть на территории России`
          )
        }
      },
      /** Поли линия OSRM [lng, lat][] — отклоняем, если много точек вне РФ */
      assertRouteInRussia(lngLats, { useCorridor = false } = {}) {
        // KZ: zero tolerance (короткий выезд через Петропавловск/Булаево не маскируем ratio)
        if (routeHitsKazakhstan(lngLats)) {
          throw new Error('Маршрут выходит за пределы России')
        }
        const limit = useCorridor
          ? ROUTE_OUTSIDE_RF_RATIO_CORRIDOR
          : ROUTE_OUTSIDE_RF_RATIO
        if (routeOutsideRussiaRatio(lngLats) > limit) {
          throw new Error('Маршрут выходит за пределы России')
        }
      },
      /**
       * Только RF-corridor vias (городские якоря Транссиба) между A/B и mid-ногами.
       * Без пользовательских mid и без АЗС — для OSRM (маршрут по РФ).
       */
      resolveCorridorVias(coordsA, coordsB, midCoords = []) {
        const mids = (Array.isArray(midCoords) ? midCoords : []).filter(c =>
          this.isValidLatLng(c)
        )
        if (!mids.length) {
          if (!needsRfCorridorVias(coordsA, coordsB)) return []
          return rfCorridorViasBetween(coordsA, coordsB)
        }
        const anchors = [coordsA, ...mids, coordsB]
        const vias = []
        for (let i = 0; i < anchors.length - 1; i++) {
          const from = anchors[i]
          const to = anchors[i + 1]
          if (!needsRfCorridorVias(from, to)) continue
          for (const wp of rfCorridorViasBetween(from, to)) {
            if (this.isNearAnyLatLng(wp, anchors, RF_VIA_NEAR_USER_M)) continue
            vias.push(wp)
          }
        }
        return vias
      },
      /**
       * OSRM vias между A и B.
       * Якоря: A → mid + выбранные АЗС (вдоль текущего path) → B.
       * Между соседними якорями — RF corridor (мягкие helpers, не в Yandex).
       * Corridor via рядом с якорем/уже добавленным via — дедуп.
       */
      resolveRouteWaypoints(coordsA, coordsB, stationWaypoints = [], midCoords = []) {
        const mids = (Array.isArray(midCoords) ? midCoords : []).filter(c =>
          this.isValidLatLng(c)
        )
        const stations = (
          Array.isArray(stationWaypoints) ? stationWaypoints : []
        ).filter(c => this.isValidLatLng(c))

        if (!mids.length && !stations.length) {
          return this.resolveCorridorVias(coordsA, coordsB, [])
        }

        // mid + АЗС в порядке вдоль маршрута (иначе OSRM зигзаг / без коридора → KZ/CN)
        const path =
          (Array.isArray(this._baseRoute) && this._baseRoute.length >= 2
            ? this._baseRoute
            : null) ||
          (Array.isArray(this._lastRoute?.route) &&
          this._lastRoute.route.length >= 2
            ? this._lastRoute.route
            : null) ||
          []

        let userStops
        if (stations.length && path.length >= 2) {
          userStops = [...mids, ...stations]
            .map((c, i) => ({ c, i, d: distanceAlongPath(path, c) }))
            .sort((a, b) => a.d - b.d || a.i - b.i)
            .map(x => x.c)
        } else {
          userStops = [...mids, ...stations]
        }

        const anchors = [coordsA, ...userStops, coordsB]
        const vias = []
        for (let i = 0; i < anchors.length - 1; i++) {
          const from = anchors[i]
          const to = anchors[i + 1]
          if (needsRfCorridorVias(from, to)) {
            for (const wp of rfCorridorViasBetween(from, to)) {
              if (this.isNearAnyLatLng(wp, anchors, RF_VIA_NEAR_USER_M)) continue
              if (this.isNearAnyLatLng(wp, vias, RF_VIA_NEAR_USER_M)) continue
              vias.push(wp)
            }
          }
          // Явный mid / АЗС — после RF helpers на ноге
          if (i < anchors.length - 2) {
            vias.push(to)
          }
        }
        return vias
      },
      /**
       * Parse OSRM routes[] → { pathLngLat, distance, duration }[], RF-filter.
       * Returns { routes, lastRfError }.
       */
      parseOsrmRoutes(rawRoutes, { useCorridor = false } = {}) {
        const routes = []
        let lastRfError = null
        let discardedRf = 0
        for (const raw of Array.isArray(rawRoutes) ? rawRoutes : []) {
          const coordinates = raw?.geometry?.coordinates
          if (!Array.isArray(coordinates) || !coordinates.length) continue
          const lngLats = coordinates
            .map(c => [Number(c[0]), Number(c[1])])
            .filter(
              ([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat)
            )
          if (!lngLats.length) continue
          try {
            this.assertRouteInRussia(lngLats, { useCorridor })
          } catch (e) {
            lastRfError = e
            discardedRf += 1
            continue
          }
          routes.push({
            pathLngLat: lngLats,
            distance: Number(raw.distance) || 0,
            duration: Number(raw.duration) || 0
          })
        }
        if (discardedRf > 0 && typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.info(
            `[FuelPlanner] OSRM: отброшено вне РФ: ${discardedRf}, принято: ${routes.length}`
          )
        }
        return { routes, lastRfError }
      },
      async fetchOsrmRaw(coordsA, coordsB, vias = [], { alternatives = false } = {}) {
        const via = (Array.isArray(vias) ? vias : []).filter(c =>
          this.isValidLatLng(c)
        )
        const parts = [coordsA, ...via, coordsB].map(c => `${c[1]},${c[0]}`)
        const altParam = alternatives ? '3' : 'false'
        const url = `${OSRM_BASE}/route/v1/driving/${parts.join(';')}?overview=full&geometries=geojson&alternatives=${altParam}&steps=false`
        let data
        try {
          data = await this.httpJson(url)
        } catch (e) {
          if (e?.status === 0 || e?.status >= 500 || e?.status === 429) {
            throw new Error(OSRM_UNAVAILABLE_MSG)
          }
          throw e
        }
        if (data?.code && data.code !== 'Ok') {
          if (data.code === 'NoRoute') {
            throw new Error('Маршрут между точками не найден. Уточните адреса А и Б.')
          }
          throw new Error(OSRM_UNAVAILABLE_MSG)
        }
        return Array.isArray(data?.routes) ? data.routes : []
      },
      /**
       * Когда OSRM дал 1 route на чистом A→B — добираем развилки через
       * mid-via со смещением перпендикулярно коридору (±15–40 км).
       */
      async fetchSyntheticRouteAlternatives(coordsA, coordsB, primary) {
        const offsetVias = syntheticAltOffsetVias(coordsA, coordsB)
        if (!offsetVias.length || !primary?.pathLngLat?.length) return []

        const settled = await Promise.all(
          offsetVias.map(async via => {
            try {
              const raw = await this.fetchOsrmRaw(coordsA, coordsB, [via], {
                alternatives: false
              })
              const { routes } = this.parseOsrmRoutes(raw, { useCorridor: false })
              return routes
            } catch (_) {
              return []
            }
          })
        )

        const out = []
        for (const batch of settled) {
          for (const cand of batch) {
            if (!isDistinctRouteAlternative(primary, cand)) continue
            if (
              out.some(
                r =>
                  Math.abs(r.distance - cand.distance) < 500 &&
                  Math.abs(r.duration - cand.duration) < 60
              )
            ) {
              continue
            }
            out.push(cand)
          }
        }
        return out
      },
      async fetchRouteOsrm(coordsA, coordsB, waypoints = []) {
        if (!this.isValidLatLng(coordsA) || !this.isValidLatLng(coordsB)) {
          throw new Error('Не удалось определить координаты для маршрута')
        }
        const via = (Array.isArray(waypoints) ? waypoints : []).filter(c =>
          this.isValidLatLng(c)
        )
        // OSRM: lon,lat;… — alternatives=3 (public demo часто всё равно 1 на РФ)
        const rawRoutes = await this.fetchOsrmRaw(coordsA, coordsB, via, {
          alternatives: true
        })
        if (!rawRoutes.length) {
          throw new Error('Маршрут между точками не найден. Уточните адреса А и Б.')
        }

        const useCorridor = via.length > 0
        const parsed = this.parseOsrmRoutes(rawRoutes, { useCorridor })
        let routes = parsed.routes

        if (!routes.length) {
          throw new Error(
            parsed.lastRfError?.message ||
              'Маршрут между точками не найден. Уточните адреса А и Б.'
          )
        }

        // Синтетические развилки только на чистом A→B (без АЗС / Transsib vias)
        if (via.length === 0 && routes.length === 1) {
          const synth = await this.fetchSyntheticRouteAlternatives(
            coordsA,
            coordsB,
            routes[0]
          )
          if (synth.length) {
            routes = [...routes, ...synth]
            routes.sort(
              (a, b) =>
                (a.duration - b.duration) || (a.distance - b.distance)
            )
            routes = routes.slice(0, SYNTH_ALT_MAX_ROUTES)
          }
        }

        return { routes }
      },
      /**
       * [lat, lng]: finite, в диапазонах; (0,0) / Null Island для маршрутов — нет.
       */
      isValidLatLng(coords) {
        if (!Array.isArray(coords) || coords.length < 2) return false
        const lat = Number(coords[0])
        const lng = Number(coords[1])
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
        // Missing DaData geo часто становится 0,0 — отклоняем
        if (lat === 0 && lng === 0) return false
        return true
      },
      /** GeoJSON path point [lng, lat] */
      isValidLngLat(coords) {
        if (!Array.isArray(coords) || coords.length < 2) return false
        const lng = Number(coords[0])
        const lat = Number(coords[1])
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
        if (lat === 0 && lng === 0) return false
        return true
      },
      // Nominatim: [lat, lon] → MapLibre GeoJSON [lon, lat]
      toLngLat(coords) {
        return [Number(coords[1]), Number(coords[0])]
      },
      removeRouteFromMap() {
        if (!this._map) return
        try {
          const altsSrc = this._map.getSource(ROUTE_ALTS_SOURCE_ID)
          if (altsSrc) altsSrc.setData(EMPTY_POINTS)
          const lineSrc = this._map.getSource('route-line')
          if (lineSrc) lineSrc.setData(EMPTY_LINE)
          const pointsSrc = this._map.getSource('route-points')
          if (pointsSrc) pointsSrc.setData(EMPTY_POINTS)
          const stationsSrc = this._map.getSource(STATIONS_SOURCE_ID)
          if (stationsSrc) stationsSrc.setData(EMPTY_POINTS)
        } catch (_) {
          /* ignore */
        }
      },
      syncRouteAlternativesOnMap() {
        if (!this._map) return
        try {
          const src = this._map.getSource(ROUTE_ALTS_SOURCE_ID)
          if (!src) return
          const features = this.routeAlternatives
            .map((alt, i) => {
              if (i === this.selectedRouteIndex) return null
              const coords = (alt?.pathLngLat || []).filter(c =>
                this.isValidLngLat(c)
              )
              if (coords.length < 2) return null
              return {
                type: 'Feature',
                properties: { routeIndex: i },
                geometry: { type: 'LineString', coordinates: coords }
              }
            })
            .filter(Boolean)
          src.setData({ type: 'FeatureCollection', features })
        } catch (_) {
          /* ignore */
        }
      },
      routeAlternativeLabel(index, alt) {
        const n = Number(index) + 1
        const duration = formatRouteDuration(alt?.duration)
        const distance = formatRouteDistance(alt?.distance)
        return `Маршрут ${n} · ${duration} · ${distance}`
      },
      async selectRouteAlternative(index) {
        if (this.isBuilding || this.isUpdatingAlternativeStations) return
        const idx = Number(index)
        if (
          !Number.isFinite(idx) ||
          idx < 0 ||
          idx >= this.routeAlternatives.length ||
          idx === this.selectedRouteIndex
        ) {
          return
        }
        const alt = this.routeAlternatives[idx]
        const last = this._lastRoute
        if (!alt?.pathLngLat?.length || !last) return
        if (!this.isValidLatLng(last.coordsA) || !this.isValidLatLng(last.coordsB)) {
          return
        }

        const savedSelected = this.snapshotSelectedStations()
        this.selectedRouteIndex = idx
        this._baseRoute = alt.pathLngLat
        try {
          this.drawRouteOnMap(alt.pathLngLat, last.coordsA, last.coordsB, {
            fitBounds: false,
            midCoords: Array.isArray(last.midCoords) ? last.midCoords : []
          })
          this.syncRouteAlternativesOnMap()
          this.isUpdatingAlternativeStations = true
          const routeStations = await this.getAlternativeStations(idx, alt.pathLngLat)
          this.applyStationsForRoute(routeStations, savedSelected, alt.pathLngLat)
          if (!this.isAlive) return
          this.syncStationsOnMap()
          this._lastRoute = {
            coordsA: last.coordsA,
            coordsB: last.coordsB,
            midCoords: Array.isArray(last.midCoords) ? last.midCoords : [],
            route: alt.pathLngLat,
            baseRoute: alt.pathLngLat,
            stations: this.snapshotSelectedStations(),
            osrmVias: Array.isArray(last.osrmVias) ? last.osrmVias : []
          }
          this.routeBuilt = true
        } catch (e) {
          if (this.isAlive) {
            this.showError(e?.message || 'Ошибка переключения маршрута')
          }
        } finally {
          if (this.isAlive) this.isUpdatingAlternativeStations = false
        }
      },
      stationsToGeoJson(stations) {
        const list = Array.isArray(stations) ? stations : []
        return {
          type: 'FeatureCollection',
          features: list
            .filter(s => this.isValidLatLng(s?.coords))
            .map(s => {
              const marker = resolveBrandMarkerColor(s.brand)
              const isFranchise = toFranchiseBool(s.is_franchise)
              return {
                type: 'Feature',
                properties: {
                  id: s.id,
                  name: s.name || '',
                  brand: s.brand || '',
                  address: s.address || '',
                  selected: s.selected ? 1 : 0,
                  isFranchise: isFranchise ? 1 : 0,
                  markerColor: marker.fill,
                  markerStroke: marker.stroke
                },
                geometry: {
                  type: 'Point',
                  coordinates: this.toLngLat(s.coords)
                }
              }
            })
        }
      },
      syncStationsOnMap() {
        if (!this._map) return
        try {
          const stations = this.mapStations
          const src = this._map.getSource(STATIONS_SOURCE_ID)
          if (src) src.setData(this.stationsToGeoJson(stations))
          this.hideStationHoverPopup()
          if (this.activeStationId != null) {
            const visibleIds = new Set(stations.map(s => String(s.id)))
            if (!visibleIds.has(String(this.activeStationId))) {
              this.activeStationId = null
            }
          }
        } catch (_) {
          /* ignore */
        }
      },
      async ensureStationCoordinatesCache() {
        if (this._stationCoordinatesCache !== null) {
          this.allApiBrands = uniqueBrandsFromApiList(this._stationCoordinatesCache)
          this.allApiFuelTypes = uniqueFuelTypesFromApiList(
            this._stationCoordinatesCache
          )
          return this._stationCoordinatesCache
        }
        if (!this._stationCoordinatesLoadPromise) {
          this._stationCoordinatesLoadPromise = (async () => {
            try {
              const res = await this.$store.dispatch('getStationCoordinatesAll')
              const list = Array.isArray(res?.data) ? res.data : []
              this._stationCoordinatesCache = list
              this.allApiBrands = uniqueBrandsFromApiList(list)
              this.allApiFuelTypes = uniqueFuelTypesFromApiList(list)
              return list
            } catch (err) {
              this._stationCoordinatesCache = null
              throw err
            } finally {
              this._stationCoordinatesLoadPromise = null
            }
          })()
        }
        return this._stationCoordinatesLoadPromise
      },
      cloneRouteStations(stations) {
        return (Array.isArray(stations) ? stations : []).map(s => ({
          ...s,
          coords: Array.isArray(s?.coords) ? [...s.coords] : s?.coords,
          prices: Array.isArray(s?.prices) ? s.prices.map(p => ({ ...p })) : [],
          fuels: Array.isArray(s?.fuels) ? s.fuels.map(p => ({ ...p })) : []
        }))
      },
      async loadRouteStations(pathLngLat, { showLoader = true } = {}) {
        if (showLoader) this.isLoadingStations = true
        let apiList = []
        try {
          apiList = await this.ensureStationCoordinatesCache()
        } catch (_) {
          // showError уже из Vuex action
          apiList = []
          this._stationCoordinatesCache = null
          this.allApiBrands = []
          this.allApiFuelTypes = []
        } finally {
          if (showLoader && this.isAlive) this.isLoadingStations = false
        }

        if (!this.isAlive) return []
        return filterStationsAlongRoute(apiList, pathLngLat)
      },
      applyStationsForRoute(routeStations, savedSelected = [], pathLngLat) {
        const alongRoute = this.cloneRouteStations(routeStations)
        const saved = Array.isArray(savedSelected) ? savedSelected : []
        const selectedIds = new Set()

        saved.forEach(savedSt => {
          const byId = alongRoute.find(s => {
            if (savedSt?.id != null && String(s.id) === String(savedSt.id)) {
              return true
            }
            if (
              savedSt?.station_id != null &&
              s.station_id != null &&
              String(s.station_id) === String(savedSt.station_id)
            ) {
              return true
            }
            return false
          })
          if (byId) {
            selectedIds.add(byId.id)
            return
          }
          if (!this.isValidLatLng(savedSt?.coords)) return
          let best = null
          let bestDist = Infinity
          alongRoute.forEach(m => {
            const d = haversineMeters(
              this.toLngLat(savedSt.coords),
              this.toLngLat(m.coords)
            )
            if (d < bestDist) {
              bestDist = d
              best = m
            }
          })
          if (best && bestDist <= 2000) selectedIds.add(best.id)
        })

        alongRoute.forEach(s => {
          s.selected = selectedIds.has(s.id)
        })

        this.stations = alongRoute
        this.selectedStations = this.sortStationsAlongPath(
          alongRoute.filter(s => s.selected),
          pathLngLat
        )
        this.activeStationId = null
      },
      resetRouteAlternativesCache(routes) {
        this._routeAlternativesSeq += 1
        this._routeAlternativeStationsCache = new Array(
          Array.isArray(routes) ? routes.length : 0
        ).fill(null)
        return this._routeAlternativesSeq
      },
      async getAlternativeStations(index, pathLngLat) {
        const idx = Number(index)
        if (
          Number.isFinite(idx) &&
          idx >= 0 &&
          idx < this._routeAlternativeStationsCache.length
        ) {
          const cached = this._routeAlternativeStationsCache[idx]
          if (Array.isArray(cached)) return this.cloneRouteStations(cached)
        }
        const computed = await this.loadRouteStations(pathLngLat, { showLoader: false })
        if (
          Number.isFinite(idx) &&
          idx >= 0 &&
          idx < this._routeAlternativeStationsCache.length
        ) {
          this._routeAlternativeStationsCache[idx] = this.cloneRouteStations(computed)
        }
        return this.cloneRouteStations(computed)
      },
      warmRouteAlternativesCache(routes, seq, skipIndex = -1) {
        const list = Array.isArray(routes) ? routes : []
        list.forEach((alt, idx) => {
          if (idx === skipIndex || !Array.isArray(alt?.pathLngLat) || !alt.pathLngLat.length) {
            return
          }
          if (Array.isArray(this._routeAlternativeStationsCache[idx])) return
          this.loadRouteStations(alt.pathLngLat, { showLoader: false })
            .then(stations => {
              if (!this.isAlive || seq !== this._routeAlternativesSeq) return
              this._routeAlternativeStationsCache[idx] = this.cloneRouteStations(stations)
            })
            .catch(() => {})
        })
      },
      async setStationsFromRoute(pathLngLat, savedSelected = []) {
        const alongRoute = await this.loadRouteStations(pathLngLat, { showLoader: true })
        if (!this.isAlive) return
        this.applyStationsForRoute(alongRoute, savedSelected, pathLngLat)
      },
      sortStationsAlongPath(stations, pathLngLat) {
        const path = pathLngLat || this._baseRoute
        return [...(stations || [])]
          .map(s => ({
            ...s,
            pathDistance:
              typeof s.pathDistance === 'number'
                ? s.pathDistance
                : distanceAlongPath(path, s.coords)
          }))
          .sort((a, b) => a.pathDistance - b.pathDistance)
      },
      snapshotSelectedStations() {
        return this.selectedStations.map(s => ({
          id: s.id,
          station_id: s.station_id != null ? s.station_id : undefined,
          name: s.name,
          coords: [Number(s.coords[0]), Number(s.coords[1])]
        }))
      },
      syncSelectedFromStations() {
        const path = this._baseRoute
        this.selectedStations = this.sortStationsAlongPath(
          this.stations.filter(s => s.selected),
          path
        )
      },
      openStationPopup(stationId) {
        const station = this.findStationById(stationId)
        if (!station) return
        this.activeStationId = station.id
      },
      closeStationPopup() {
        this.activeStationId = null
      },
      addStationFromPopup() {
        if (this.activeStationId == null) return
        this.addStationToRoute(this.activeStationId)
      },
      removeStationFromPopup() {
        if (this.activeStationId == null) return
        this.removeStationFromRoute(this.activeStationId)
      },
      findStationById(stationId) {
        if (stationId == null || stationId === '') return null
        const key = String(stationId)
        return this.stations.find(s => String(s.id) === key) || null
      },
      async addStationToRoute(stationId) {
        const station = this.findStationById(stationId)
        if (!station || station.selected) return
        if (!this.routeBuilt || !this._lastRoute) {
          this.showError('Сначала постройте маршрут')
          return
        }

        station.selected = true
        this.syncSelectedFromStations()
        this.syncStationsOnMap()
        const ok = await this.rebuildRouteWithSelectedStations()
        if (!ok) {
          station.selected = false
          this.syncSelectedFromStations()
          this.syncStationsOnMap()
        }
      },
      async removeStationFromRoute(stationId) {
        const station = this.findStationById(stationId)
        if (!station || !station.selected) {
          this.closeStationPopup()
          return
        }
        if (!this.routeBuilt || !this._lastRoute) {
          station.selected = false
          this.syncSelectedFromStations()
          this.syncStationsOnMap()
          this.closeStationPopup()
          return
        }

        station.selected = false
        this.syncSelectedFromStations()
        this.syncStationsOnMap()
        const ok = await this.rebuildRouteWithSelectedStations()
        if (!ok) {
          station.selected = true
          this.syncSelectedFromStations()
          this.syncStationsOnMap()
        }
      },
      async rebuildRouteWithSelectedStations({ fitBounds = true } = {}) {
        const last = this._lastRoute
        if (!last || !this.isValidLatLng(last.coordsA) || !this.isValidLatLng(last.coordsB)) {
          return false
        }

        const midCoords = (
          Array.isArray(last.midCoords) ? last.midCoords : this.currentMidCoordsList()
        ).filter(c => this.isValidLatLng(c))
        const stationWaypoints = this.selectedStations
          .map(s => s.coords)
          .filter(c => this.isValidLatLng(c))
        const waypoints = this.resolveRouteWaypoints(
          last.coordsA,
          last.coordsB,
          stationWaypoints,
          midCoords
        )

        this.isBuilding = true
        try {
          const result = await withTimeout(
            this.fetchRouteOsrm(last.coordsA, last.coordsB, waypoints),
            ROUTE_BUILD_TIMEOUT_MS,
            'Превышено время ожидания построения маршрута (~25 с).'
          )
          if (!this.isAlive || !this._map) return false

          const routes = result?.routes || []
          if (!routes.length) return false
          this.routeAlternatives = routes
          const altsSeq = this.resetRouteAlternativesCache(routes)
          this.selectedRouteIndex = 0
          const path = routes[0].pathLngLat

          this.drawRouteOnMap(path, last.coordsA, last.coordsB, {
            fitBounds,
            midCoords
          })
          this.syncRouteAlternativesOnMap()
          this._lastRoute = {
            coordsA: last.coordsA,
            coordsB: last.coordsB,
            midCoords,
            route: path,
            baseRoute: this._baseRoute || last.baseRoute || path,
            stations: this.snapshotSelectedStations(),
            osrmVias: waypoints
          }
          this.routeBuilt = true
          this.syncStationsOnMap()
          this.warmRouteAlternativesCache(routes, altsSeq, 0)
          return true
        } catch (e) {
          if (this.isAlive) {
            this.showError(e?.message || 'Ошибка перестроения маршрута')
          }
          return false
        } finally {
          if (this.isAlive) this.isBuilding = false
        }
      },
      drawRouteOnMap(pathLngLat, coordsA, coordsB, options = {}) {
        if (!this._map) return

        const fitBounds = options.fitBounds !== false
        const midCoords = (
          Array.isArray(options.midCoords)
            ? options.midCoords
            : this.lastRouteMidCoords()
        ).filter(c => this.isValidLatLng(c))
        const safePath = (Array.isArray(pathLngLat) ? pathLngLat : []).filter(c =>
          this.isValidLngLat(c)
        )
        if (!safePath.length || !this.isValidLatLng(coordsA) || !this.isValidLatLng(coordsB)) {
          throw new Error('Маршрут содержит некорректные координаты.')
        }

        const lngLatA = this.toLngLat(coordsA)
        const lngLatB = this.toLngLat(coordsB)

        const lineSrc = this._map.getSource('route-line')
        const pointsSrc = this._map.getSource('route-points')
        if (!lineSrc || !pointsSrc) {
          throw new Error('Слои маршрута ещё не готовы.')
        }

        lineSrc.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: safePath }
        })

        const features = [
          {
            type: 'Feature',
            properties: { label: 'A' },
            geometry: { type: 'Point', coordinates: lngLatA }
          },
          ...midCoords.map((coords, idx) => ({
            type: 'Feature',
            properties: { label: midPointLabelAt(idx) },
            geometry: { type: 'Point', coordinates: this.toLngLat(coords) }
          })),
          {
            type: 'Feature',
            properties: { label: 'B' },
            geometry: { type: 'Point', coordinates: lngLatB }
          }
        ]
        pointsSrc.setData({ type: 'FeatureCollection', features })

        if (!fitBounds) return

        try {
          const bounds = safePath.reduce(
            (b, c) => b.extend(c),
            new maplibregl.LngLatBounds(safePath[0], safePath[0])
          )
          bounds.extend(lngLatA)
          bounds.extend(lngLatB)
          midCoords.forEach(c => bounds.extend(this.toLngLat(c)))
          this._map.fitBounds(bounds, { padding: 40, animate: false })
        } catch (_) {
          this._map.jumpTo({ center: lngLatA, zoom: 9 })
        }
      },
      async buildRoute() {
        if (!this._buildingFromFavorite) {
          this.activeFavoriteId = null
        }

        const pointA = String(this.pointA || '').trim()
        const pointB = String(this.pointB || '').trim()
        const midTexts = this.midPoints.map(m => String(m.text || '').trim())

        if (!pointA || !pointB || midTexts.some(t => !t)) {
          this.showError(
            this.midPoints.length
              ? 'Укажите все точки маршрута'
              : 'Укажите точку А и точку Б'
          )
          return
        }
        if (!this.canBuildRoute || !this._map) {
          this.showError(this.mapLoadError || 'Карта ещё не готова')
          return
        }

        this.isBuilding = true
        this.invalidateBuiltRoute()
        this.removeRouteFromMap()
        this.suggestA.open = false
        this.suggestA.anchorStyle = null
        this.suggestB.open = false
        this.suggestB.anchorStyle = null
        this.midPoints.forEach(m => {
          m.suggest.open = false
          m.suggest.anchorStyle = null
        })

        const destLabel = this.destinationLabel

        try {
          const midJobs = this.midPoints.map((mid, idx) =>
            this.resolvePointForBuild(
              mid.key,
              midTexts[idx],
              `точки ${midPointLabelAt(idx)}`
            )
          )
          const [geoA, geoB, ...geoMids] = await withTimeout(
            Promise.all([
              this.resolvePointForBuild('A', pointA, 'точки А'),
              this.resolvePointForBuild('B', pointB, `точки ${destLabel}`),
              ...midJobs
            ]),
            ROUTE_BUILD_TIMEOUT_MS,
            'Превышено время ожидания геокодирования (~25 с).'
          )

          if (!this.isAlive || !this._map) return

          if (!this.isValidLatLng(geoA?.coords)) {
            throw new Error('Не удалось определить координаты точки А')
          }
          if (!this.isValidLatLng(geoB?.coords)) {
            throw new Error(
              `Не удалось определить координаты точки ${destLabel}`
            )
          }
          for (let i = 0; i < geoMids.length; i++) {
            if (!this.isValidLatLng(geoMids[i]?.coords)) {
              throw new Error(
                `Не удалось определить координаты точки ${midPointLabelAt(i)}`
              )
            }
          }

          // Маршруты только по РФ: эндпоинты вне приблизительного bbox — abort
          this.assertPointInRussia(geoA.coords, 'А')
          this.assertPointInRussia(geoB.coords, destLabel)
          for (let i = 0; i < geoMids.length; i++) {
            this.assertPointInRussia(geoMids[i].coords, midPointLabelAt(i))
          }

          // Сначала coords (подпись через v-model не должна их сбрасывать)
          this._coordsA = geoA.coords
          this._coordsB = geoB.coords
          if (geoA.text && geoA.text !== this.pointA) this.pointA = geoA.text
          if (geoB.text && geoB.text !== this.pointB) this.pointB = geoB.text
          geoMids.forEach((geo, i) => {
            const mid = this.midPoints[i]
            if (!mid) return
            this.setPointCoords(mid.key, geo.coords)
            if (geo.text && geo.text !== mid.text) mid.text = geo.text
          })

          const midCoords = geoMids.map(g => g.coords)
          const osrmVias = this.resolveRouteWaypoints(
            geoA.coords,
            geoB.coords,
            [],
            midCoords
          )
          const result = await withTimeout(
            this.fetchRouteOsrm(geoA.coords, geoB.coords, osrmVias),
            ROUTE_BUILD_TIMEOUT_MS,
            'Превышено время ожидания построения маршрута (~25 с).'
          )

          if (!this.isAlive || !this._map) return

          const routes = result?.routes || []
          if (!routes.length) {
            throw new Error('Маршрут между точками не найден. Уточните адреса А и Б.')
          }
          this.routeAlternatives = routes
          const altsSeq = this.resetRouteAlternativesCache(routes)
          this.selectedRouteIndex = 0
          const path = routes[0].pathLngLat

          this._baseRoute = path
          const routeStations = await this.loadRouteStations(path, { showLoader: true })
          if (
            this.isAlive &&
            Array.isArray(this._routeAlternativeStationsCache) &&
            this._routeAlternativeStationsCache.length
          ) {
            this._routeAlternativeStationsCache[0] = this.cloneRouteStations(routeStations)
          }
          this.applyStationsForRoute(routeStations, [], path)
          if (!this.isAlive || !this._map) return

          this.drawRouteOnMap(path, geoA.coords, geoB.coords, { midCoords })
          this.syncRouteAlternativesOnMap()
          this.syncStationsOnMap()
          this._lastRoute = {
            coordsA: geoA.coords,
            coordsB: geoB.coords,
            midCoords,
            route: path,
            baseRoute: path,
            stations: [],
            osrmVias
          }
          this.routeBuilt = true
          this.warmRouteAlternativesCache(routes, altsSeq, 0)
          await this.applyPendingFavoriteAfterBuild()
        } catch (e) {
          if (this.isAlive) {
            this._lastRoute = null
            this._baseRoute = null
            this.routeAlternatives = []
            this.selectedRouteIndex = 0
            this.clearStations()
            this.showError(e?.message || 'Ошибка построения маршрута')
          }
        } finally {
          if (this.isAlive) {
            this.isBuilding = false
          }
        }
      },
      toApiGeoPoint(coords, address) {
        const point = {
          lat: Number(coords[0]),
          lon: Number(coords[1])
        }
        const addr = String(address || '').trim()
        if (addr) point.address = addr
        return point
      },
      async loadRoutePlannerFavorites() {
        try {
          await this.$store.dispatch('getRoutePlannerFavorites')
        } catch (_) {
          // showError уже из Vuex action
        }
      },
      favoritePointLabel(point) {
        if (!point || typeof point !== 'object') return ''
        const addr = String(point.address || '').trim()
        if (addr) return addr
        const lat = Number(point.lat)
        const lon = Number(point.lon ?? point.lng)
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return `${lat.toFixed(5)}, ${lon.toFixed(5)}`
        }
        return ''
      },
      /** Короткое имя для списка избранного: город из address, иначе первая осмысленная часть */
      favoriteRouteShortLabel(address, fallback = '') {
        const raw = String(address || '').trim()
        if (!raw) return fallback
        const cityPrefix = /^(г\.?\s*|город\s+)/i
        const parts = raw
          .split(',')
          .map(p => p.trim())
          .filter(Boolean)
        if (parts.length === 1) {
          return parts[0].replace(cityPrefix, '').trim() || parts[0]
        }
        const cityPart = parts.find(p => cityPrefix.test(p))
        if (cityPart) {
          return cityPart.replace(cityPrefix, '').trim() || cityPart
        }
        const streetRe =
          /^(ул\.|улица|пр-?[т]?\.?|проспект|пер\.|переулок|ш\.|шоссе|наб\.|бул\.|б-р)/i
        const skipRe = /^(россия|russia|рф)$/i
        const good = parts.filter(
          p => !streetRe.test(p) && !skipRe.test(p) && !/^\d/.test(p)
        )
        if (good.length) return good[0]
        return parts[0]
      },
      favoriteWaypointRouteLabel(wp) {
        if (!wp || typeof wp !== 'object') return ''
        const addr = isViaFavoriteWaypoint(wp)
          ? viaAddressFromFavorite(wp)
          : String(wp.address || '').trim()
        if (addr) return this.favoriteRouteShortLabel(addr)
        const lat = Number(wp.lat)
        const lon = Number(wp.lon ?? wp.lng)
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return `${lat.toFixed(5)}, ${lon.toFixed(5)}`
        }
        return isViaFavoriteWaypoint(wp) ? 'Промежуточная' : 'АЗС'
      },
      favoriteRouteLabel(fav) {
        const origin = this.favoriteRouteShortLabel(
          this.favoritePointLabel(fav?.origin),
          'Точка А'
        )
        const destination = this.favoriteRouteShortLabel(
          this.favoritePointLabel(fav?.destination),
          'Точка Б'
        )
        const mids = (Array.isArray(fav?.waypoints) ? fav.waypoints : [])
          .filter(isViaFavoriteWaypoint)
          .map(wp => this.favoriteWaypointRouteLabel(wp))
          .filter(Boolean)
        return [origin, ...mids, destination].join(' → ')
      },
      favoriteStationsCountLabel(fav) {
        const n = (Array.isArray(fav?.waypoints) ? fav.waypoints : []).filter(
          wp => !isViaFavoriteWaypoint(wp)
        ).length
        const mod10 = n % 10
        const mod100 = n % 100
        let word = 'заправок'
        if (mod10 === 1 && mod100 !== 11) word = 'заправка'
        else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
          word = 'заправки'
        }
        return `${n} ${word}`
      },
      async applyFavorite(fav) {
        if (!fav || this.isBuilding || this.isSaving) return

        const coordsA = this.normalizeLatLng(fav.origin)
        const coordsB = this.normalizeLatLng(fav.destination)
        if (!coordsA || !coordsB) {
          this.showError('В избранном нет координат точек А и Б')
          return
        }

        const textA =
          this.favoritePointLabel(fav.origin) ||
          String(this.pointA || '').trim()
        const textB =
          this.favoritePointLabel(fav.destination) ||
          String(this.pointB || '').trim()

        if (textA) this.pointA = textA
        if (textB) this.pointB = textB
        this._coordsA = coordsA
        this._coordsB = coordsB

        const allWp = Array.isArray(fav.waypoints) ? fav.waypoints : []
        const viaWps = allWp.filter(isViaFavoriteWaypoint)
        const azsWps = allWp.filter(wp => !isViaFavoriteWaypoint(wp))
        this.replaceMidPointsFromFavoriteVias(viaWps)

        // Фильтры не восстанавливаем: пустой = показать все АЗС/цены без галочек в UI
        this.selectedBrandFilters = []
        this.selectedFuelTypeFilters = []
        this._pendingFavoriteApply = {
          waypoints: azsWps
        }

        this.activeFavoriteId = fav.id
        this._buildingFromFavorite = true
        try {
          // Тот же путь, что кнопка «Построить маршрут»: OSRM + карта + АЗС;
          // АЗС-waypoints — в applyPendingFavoriteAfterBuild после коридора.
          await this.buildRoute()
        } finally {
          this._buildingFromFavorite = false
        }
      },
      async applyPendingFavoriteAfterBuild() {
        const pending = this._pendingFavoriteApply
        if (!pending) return
        this._pendingFavoriteApply = null

        const waypoints = (pending.waypoints || [])
          .map(wp => this.normalizeLatLng(wp))
          .filter(c => this.isValidLatLng(c))
        if (!waypoints.length || !this.stations.length) return

        const MATCH_RADIUS_M = 80
        let matched = 0
        waypoints.forEach(wp => {
          let best = null
          let bestDist = Infinity
          this.stations.forEach(station => {
            if (!this.isValidLatLng(station.coords)) return
            const d = haversineMeters(
              this.toLngLat(wp),
              this.toLngLat(station.coords)
            )
            if (d < bestDist) {
              bestDist = d
              best = station
            }
          })
          if (best && bestDist <= MATCH_RADIUS_M && !best.selected) {
            best.selected = true
            matched += 1
          }
        })

        if (!matched) return
        this.syncSelectedFromStations()
        this.syncStationsOnMap()
        await this.rebuildRouteWithSelectedStations({ fitBounds: true })
      },
      openDeleteFavoriteModal(id) {
        if (id == null || this.isDeletingFavorite) return
        this.favoriteToDeleteId = id
        document.addEventListener('keydown', this.onDeleteFavoriteKeydown)
      },
      closeDeleteFavoriteModal() {
        if (this.isDeletingFavorite) return
        this.favoriteToDeleteId = null
        document.removeEventListener('keydown', this.onDeleteFavoriteKeydown)
      },
      onDeleteFavoriteKeydown(e) {
        if (e.key === 'Escape') this.closeDeleteFavoriteModal()
      },
      async confirmDeleteFavorite() {
        const id = this.favoriteToDeleteId
        if (id == null || this.isDeletingFavorite) return
        this.isDeletingFavorite = true
        try {
          await this.$store.dispatch('deleteRoutePlannerFavorite', id)
          if (this.activeFavoriteId === id) {
            this.activeFavoriteId = null
          }
          if (this.isAlive) {
            this.favoriteToDeleteId = null
            document.removeEventListener('keydown', this.onDeleteFavoriteKeydown)
          }
        } catch (_) {
          // showError уже из Vuex action
        } finally {
          if (this.isAlive) this.isDeletingFavorite = false
        }
      },
      defaultFavoriteNameFromPoints() {
        const pointA = this.favoriteRouteShortLabel(
          String(this.pointA || '').trim()
        )
        const pointB = this.favoriteRouteShortLabel(
          String(this.pointB || '').trim()
        )
        const mids = this.midPoints
          .map(m =>
            this.favoriteRouteShortLabel(String(m.text || '').trim())
          )
          .filter(Boolean)
        if (pointA && pointB) {
          return [pointA, ...mids, pointB].join(' → ')
        }
        return pointA || pointB || ''
      },
      openSaveFavoriteModal({ update = false } = {}) {
        if (this.isBuilding || this.isSaving) return
        if (!this.routeBuilt || !this._lastRoute) {
          this.showError('Сначала постройте маршрут')
          return
        }

        const { coordsA, coordsB } = this._lastRoute
        if (!this.isValidLatLng(coordsA) || !this.isValidLatLng(coordsB)) {
          this.showError('Сначала постройте маршрут')
          return
        }

        if (update && this.activeFavoriteId == null) {
          this.showError('Сначала выберите избранный маршрут')
          return
        }

        const mode = update ? 'update' : 'create'
        let name = this.defaultFavoriteNameFromPoints()
        if (update && this.activeFavoriteId != null) {
          const current = this.routePlannerFavorites.find(
            f => f.id === this.activeFavoriteId
          )
          const existing = String(current?.name || '').trim()
          if (existing) name = existing
        }

        this.saveFavoriteMode = mode
        this.saveFavoriteName = name
        document.addEventListener('keydown', this.onSaveFavoriteKeydown)
        this.$nextTick(() => {
          const input = this.$refs.saveFavoriteNameInput
          if (input && typeof input.focus === 'function') {
            input.focus()
            if (typeof input.select === 'function') input.select()
          }
        })
      },
      closeSaveFavoriteModal() {
        if (this.isSaving) return
        this.saveFavoriteMode = null
        this.saveFavoriteName = ''
        document.removeEventListener('keydown', this.onSaveFavoriteKeydown)
      },
      onSaveFavoriteKeydown(e) {
        if (e.key === 'Escape') this.closeSaveFavoriteModal()
      },
      async confirmSaveFavorite() {
        if (this.isSaving || this.saveFavoriteMode == null) return

        const name = String(this.saveFavoriteName || '').trim()
        if (!name) {
          this.showError('Укажите название маршрута')
          return
        }

        if (!this.routeBuilt || !this._lastRoute) {
          this.showError('Сначала постройте маршрут')
          return
        }

        const { coordsA, coordsB } = this._lastRoute
        if (!this.isValidLatLng(coordsA) || !this.isValidLatLng(coordsB)) {
          this.showError('Сначала постройте маршрут')
          return
        }

        const updateId =
          this.saveFavoriteMode === 'update' ? this.activeFavoriteId : null
        if (this.saveFavoriteMode === 'update' && updateId == null) {
          this.showError('Сначала выберите избранный маршрут')
          return
        }

        const pointA = String(this.pointA || '').trim()
        const pointB = String(this.pointB || '').trim()

        const body = {
          name,
          origin: this.toApiGeoPoint(coordsA, pointA),
          destination: this.toApiGeoPoint(coordsB, pointB),
          corridor_km: 5
        }

        // Address-via сначала (префикс via:), затем выбранные АЗС — тот же массив waypoints
        const waypoints = []
        const midCoords = (
          Array.isArray(this._lastRoute?.midCoords)
            ? this._lastRoute.midCoords
            : this.currentMidCoordsList()
        ).filter(c => this.isValidLatLng(c))
        midCoords.forEach((coords, i) => {
          const mid = this.midPoints[i]
          const addr = String(mid?.text || '').trim()
          waypoints.push(this.toApiViaPoint(coords, addr))
        })
        this.selectedStations
          .filter(s => this.isValidLatLng(s.coords))
          .forEach(s => {
            waypoints.push(this.toApiGeoPoint(s.coords, s.name))
          })
        if (waypoints.length) {
          body.waypoints = waypoints
        }

        if (this.selectedBrandFilters.length) {
          body.network_filter = { brands: [...this.selectedBrandFilters] }
        }

        const role = Number(
          localStorage.getItem('role') || this.myUser?.type || 0
        )
        if (role === 1 || role === 2) {
          const customerId =
            this.myUser?.customer_id ?? this.myUser?.customer?.id
          if (customerId != null && customerId !== '') {
            body.customer_id = customerId
          }
        }

        this.isSaving = true
        try {
          if (updateId != null) {
            await this.$store.dispatch('updateRoutePlannerFavorite', {
              id: updateId,
              data: body
            })
            if (this.isAlive) {
              this.showError('Избранное обновлено', true)
            }
          } else {
            await this.$store.dispatch('createRoutePlannerFavorite', body)
            if (this.isAlive) {
              this.showError('Маршрут сохранён в избранное', true)
            }
          }
          if (this.isAlive) {
            this.saveFavoriteMode = null
            this.saveFavoriteName = ''
            document.removeEventListener('keydown', this.onSaveFavoriteKeydown)
          }
        } catch (_) {
          // showError уже из Vuex action
        } finally {
          if (this.isAlive) this.isSaving = false
        }
      },
      /**
       * Яндекс.Карты: rtext = lat,lon~…~lat,lon; rtt=auto
       * Только A → промежуточные (mid + АЗС вдоль пути) → B (без RF-corridor / sparse).
       */
      buildYandexMapsRouteUrl(coordsA, coordsB, waypoints = []) {
        const points = [coordsA, ...(waypoints || []), coordsB].filter(c =>
          this.isValidLatLng(c)
        )
        const rtext = points
          .map(c => `${Number(c[0])},${Number(c[1])}`)
          .join('~')
        return `https://yandex.ru/maps/?rtext=${rtext}&rtt=auto`
      },
      /**
       * Промежуточные для Яндекс rtext: mid + АЗС, все вдоль пути маршрута.
       * Без RF-corridor auto-cities.
       */
      mergeYandexRouteWaypoints(
        coordsA,
        coordsB,
        midCoords,
        stationCoords,
        pathLngLat
      ) {
        const mids = (Array.isArray(midCoords) ? midCoords : []).filter(c =>
          this.isValidLatLng(c)
        )
        const stations = (Array.isArray(stationCoords) ? stationCoords : []).filter(
          c => this.isValidLatLng(c)
        )
        const path = Array.isArray(pathLngLat) ? pathLngLat : []
        let stops = [...mids, ...stations]
        if (path.length >= 2 && stops.length > 1) {
          stops = stops
            .map((c, i) => ({ c, i, d: distanceAlongPath(path, c) }))
            .sort((a, b) => a.d - b.d || a.i - b.i)
            .map(x => x.c)
        }
        const out = []
        const pushDeduped = c => {
          if (!this.isValidLatLng(c)) return
          if (
            this.isNearAnyLatLng(c, [coordsA, coordsB, ...out], YANDEX_VIA_DEDUP_M)
          ) {
            return
          }
          out.push(c)
        }
        stops.forEach(pushDeduped)
        return out
      },
      /** URL для кнопок «Скопировать» / «Открыть»; null если маршрута нет. */
      getYandexMapsRouteUrl() {
        if (!this.canOpenInYandexMaps || !this._lastRoute) return null
        const last = this._lastRoute
        const { coordsA, coordsB } = last
        if (!this.isValidLatLng(coordsA) || !this.isValidLatLng(coordsB)) {
          return null
        }
        const midCoords = this.lastRouteMidCoords()
        const stationCoords = this.selectedStations
          .map(s => s.coords)
          .filter(c => this.isValidLatLng(c))
        const path =
          (Array.isArray(last.route) && last.route.length >= 2
            ? last.route
            : null) ||
          (Array.isArray(this._baseRoute) && this._baseRoute.length >= 2
            ? this._baseRoute
            : null) ||
          []
        const waypoints = this.mergeYandexRouteWaypoints(
          coordsA,
          coordsB,
          midCoords,
          stationCoords,
          path
        )
        return this.buildYandexMapsRouteUrl(coordsA, coordsB, waypoints)
      },
      copyTextToClipboard(text) {
        if (navigator.clipboard?.writeText) {
          return navigator.clipboard.writeText(text)
        }
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        if (!ok) return Promise.reject(new Error('copy failed'))
        return Promise.resolve()
      },
      async copyYandexMapsRouteLink() {
        const url = this.getYandexMapsRouteUrl()
        if (!url) {
          this.showError('Сначала постройте маршрут')
          return
        }
        try {
          await this.copyTextToClipboard(url)
          if (!this.isAlive) return
          this.yandexLinkCopied = true
          this.showError('Скопировано', true)
          clearTimeout(this._yandexLinkCopiedTimer)
          this._yandexLinkCopiedTimer = setTimeout(() => {
            if (this.isAlive) this.yandexLinkCopied = false
          }, 2000)
        } catch (_) {
          if (this.isAlive) {
            this.showError('Не удалось скопировать ссылку')
          }
        }
      },
      openInYandexMaps() {
        const url = this.getYandexMapsRouteUrl()
        if (!url) {
          this.showError('Сначала постройте маршрут')
          return
        }
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../styles/variables';

  .page-container--map-ui-hidden {
    overflow: hidden;

    .page-content {
      height: 100%;
      overflow: hidden;
    }
  }

  .fuel-planner {
    position: relative;
    display: flex;
    gap: 24px;
    min-height: 420px;
    padding-top: 24px;
    box-sizing: border-box;

    @media screen and (max-width: 1000px) {
      flex-direction: column;
      height: auto;
      min-height: unset;
      padding: 0 15px;
      overflow-x: hidden;
      overflow-y: visible;
    }

    &--map-expanded {
      height: 100%;
      min-height: 0;
      padding-top: 0;
      overflow: hidden;
      gap: 0;

      @media screen and (max-width: 1000px) {
        height: 100%;
        min-height: 0;
        padding: 0;
        overflow: hidden;
      }
    }
  }

  .fuel-planner__form {
    flex: 0 0 320px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 320px;
    min-height: 0;
    align-self: stretch;

    @media screen and (max-width: 1000px) {
      flex: none;
      width: 100%;
      max-width: none;
      min-height: unset;
      align-self: auto;
    }

    &--expanded-overlay {
      position: absolute;
      left: 12px;
      top: 12px;
      bottom: 12px;
      z-index: 3;
      width: 320px;
      max-width: calc(100% - 24px);
      flex: none;
      align-self: auto;
      padding: 12px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.97);
      border: 1px solid #e6eaee;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
      box-sizing: border-box;
      overflow: auto;
      pointer-events: auto;
      -webkit-overflow-scrolling: touch;

      @media screen and (max-width: 1000px) {
        left: 10px;
        right: 10px;
        top: 64px;
        bottom: auto;
        width: auto;
        max-width: none;
        max-height: min(58dvh, calc(100% - 76px));
        padding: 12px 12px 14px;
      }
    }
  }

  .fuel-planner__mode-tabs {
    display: flex;
    gap: 4px;
    padding: 3px;
    border-radius: 10px;
    background: #eef1f4;
    flex: none;
  }

  .fuel-planner__mode-tab {
    flex: 1;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: $grey_1;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &--active {
      background: #fff;
      color: $black;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  .fuel-planner__region-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: none;
  }

  .fuel-planner__region-select {
    position: relative;
    z-index: 6;
    width: 100%;
    overflow: visible;

    /* Как .fuel-planner__filter-trigger / input: 38px, #c1c7cf, radius 4px */
    :deep(.fuel-planner__region-vselect) {
      position: relative;
      width: 100%;
      background: #fff;
      font-size: 14px;
      color: $black;
    }

    :deep(.vs__dropdown-toggle) {
      position: relative;
      display: flex;
      align-items: flex-start;
      min-height: 38px;
      height: auto;
      padding: 4px 10px;
      border: 1px solid #c1c7cf;
      border-radius: 4px;
      background: #fff;
      box-sizing: border-box;
      cursor: pointer;
      transition: border-color 0.15s ease;

      &:hover {
        border-color: #a8b0ba;
      }
    }

    :deep(.vs--open .vs__dropdown-toggle) {
      border-color: #6db534;
    }

    :deep(.vs__selected-options) {
      position: relative;
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      flex-wrap: wrap;
      gap: 4px;
      min-width: 0;
      min-height: 28px;
      height: auto;
      padding: 0;
      margin: 0;
      overflow: visible;
    }

    /*
     * Глобальный .input-wrap .vs__search { position:absolute; top:0; height:calc(100%-10px) }
     * ломает ряд multiple — держим search в потоке flex, как у чипов.
     */
    :deep(.vs__search),
    :deep(.vs__search:focus) {
      position: relative !important;
      top: auto !important;
      left: auto !important;
      right: auto !important;
      bottom: auto !important;
      flex: 1 1 80px;
      width: 0 !important;
      min-width: 80px;
      max-width: 100%;
      height: 28px !important;
      margin: 0 !important;
      padding: 0 2px !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      outline: none !important;
      line-height: 28px;
      font-size: 14px;
      font-weight: 400;
      color: $black;
      background: transparent !important;
      text-align: left;
      z-index: 2;
      appearance: none;
      -webkit-appearance: none;
    }

    :deep(.vs__search::placeholder) {
      color: $grey_1;
      opacity: 1;
    }

    :deep(.vs__selected) {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      margin: 0;
      padding: 0 6px;
      border: 1px solid #c1c7cf;
      border-radius: 4px;
      background: #f4f6f8;
      line-height: 22px;
      font-size: 13px;
      font-weight: 400;
      color: $black;
      white-space: nowrap;
    }

    :deep(.vs__deselect) {
      display: inline-flex;
      margin-left: 4px;
      fill: $grey_2;
    }

    :deep(.vs__actions) {
      position: relative;
      z-index: 3;
      display: flex;
      align-items: center;
      align-self: center;
      padding: 0 0 0 4px;
    }

    :deep(.vs__clear),
    :deep(.vs__open-indicator) {
      fill: $grey_2;
    }

    /* Глобальный .vs__dropdown-menu { top: 43px } даёт дыру — якорим к низу контрола */
    :deep(.vs__dropdown-menu) {
      position: absolute !important;
      top: calc(100% + 4px) !important;
      left: 0 !important;
      width: 100% !important;
      min-width: 100% !important;
      margin: 0 !important;
      padding: 4px 0;
      border: 1px solid #c1c7cf;
      border-radius: 4px;
      background: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      z-index: 30;
      box-sizing: border-box;
    }

    :deep(.vs__dropdown-option) {
      padding: 8px 12px;
      line-height: 1.3;
      font-size: 14px;
      text-align: left;
      white-space: normal;
    }

    /* vue-select ставит --selected, но в CSS библиотеки стиля нет */
    :deep(.vs__dropdown-option--selected) {
      background: rgba(109, 181, 52, 0.12);
      color: $black;
      font-weight: 500;
    }

    :deep(.vs__dropdown-option--selected.vs__dropdown-option--highlight) {
      background: rgba(109, 181, 52, 0.2);
      color: $black;
    }
  }

  .fuel-planner__points {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 180px;
    overflow-x: hidden;
    overflow-y: auto;
    flex: none;
    min-height: 0;
  }

  .fuel-planner__suggest-wrap {
    position: relative;
    flex: none;

    // 6 + 20 + 6 + border 1×2 = 34 (border-box)
    & > input {
      height: 34px;
      padding-top: 6px;
      padding-bottom: 6px;
    }
  }

  .fuel-planner__mid-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex: none;
  }

  .fuel-planner__mid-input {
    flex: 1;
    min-width: 0;
  }

  .fuel-planner__mid-remove,
  .fuel-planner__mid-add {
    flex: none;
    align-self: flex-end;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    margin: 0;
    padding: 0;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    background: #fff;
    color: #6b6c6d;
    font-size: 22px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: $back;
      color: $black;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .fuel-planner__mid-add {
    color: #6db534;
    font-size: 24px;
    font-weight: 500;

    &:hover:not(:disabled) {
      border-color: #6db534;
      color: #5a9a2c;
    }
  }

  .fuel-planner__hint {
    flex: none;
    margin: 0;
    padding: 0;
    font-size: 13px;
    line-height: 1.4;
    color: #6b6c6d;
  }

  .fuel-planner__suggest-list {
    position: fixed;
    z-index: 1100;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
  }

  .fuel-planner__suggest-item {
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $black;
    cursor: pointer;

    &:hover {
      background: $back;
    }
  }

  .btn-primary {
    box-sizing: border-box;
    min-height: 34px;
    height: auto;
    padding-top: 4px;
    padding-bottom: 4px;
    line-height: 24px;
    white-space: normal;
    cursor: pointer;
  }

  .fuel-planner__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    flex: none;

    .btn-primary {
      width: 100%;
      flex: none;
      height: 34px;
      box-sizing: border-box;
    }

    @media screen and (max-width: 1000px) {
      .btn-primary {
        height: auto;
        min-height: 40px;
        padding-top: 10px;
        padding-bottom: 10px;
        white-space: normal;
        line-height: 1.25;
      }
    }
  }

  .fuel-planner__actions-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .btn-primary {
      flex: 1 1 0;
      width: auto;
      min-width: 0;
      padding-left: 8px;
      padding-right: 8px;
      white-space: normal;
      line-height: 1.15;
    }

    @media screen and (max-width: 1000px) {
      flex-direction: column;

      .btn-primary {
        flex: none;
        width: 100%;
        min-width: 0;
        padding-left: 12px;
        padding-right: 12px;
      }
    }
  }

  .fuel-planner__route-alts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: none;
  }

  .fuel-planner__route-alt {
    box-sizing: border-box;
    width: 100%;
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    text-align: left;
    color: $black;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: $back;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--selected {
      border-color: #6db534;
      background: rgba(109, 181, 52, 0.08);
      box-shadow: inset 0 0 0 1px #6db534;
    }
  }

  .fuel-planner__favorites {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: auto;
    padding: 16px;
    box-sizing: border-box;
  }

  .fuel-planner__favorites-title {
    flex: none;
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: $grey_1;
    text-align: left;
  }

  .fuel-planner__favorites-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .fuel-planner__favorites-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #e6eaee;
    border-radius: 6px;

    &--selected {
      border-color: #6db534;
      background: rgba(109, 181, 52, 0.12);
    }
  }

  .fuel-planner__favorites-apply {
    flex: 1;
    min-width: 0;
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }

  .fuel-planner__favorites-name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: $black;
  }

  .fuel-planner__favorites-route {
    font-size: 12px;
    line-height: 1.35;
    color: $grey_2;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
  }

  .fuel-planner__favorites-stations {
    font-size: 12px;
    line-height: 1.3;
    color: $grey_1;
  }

  .fuel-planner__favorites-actions {
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  .fuel-planner__favorites-delete {
    flex: none;
    appearance: none;
    border: none;
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    box-sizing: border-box;
    min-height: 34px;
    height: auto;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    white-space: normal;
    line-height: 1.2;
    text-align: right;
    color: $orange;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .fuel-planner__found {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin-top: 4px;

    @media screen and (max-width: 1000px) {
      flex: none;
      min-height: unset;
    }
  }

  .fuel-planner__filters-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-bottom: 8px;
  }

  .fuel-planner__brand-filter {
    flex: none;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    margin-bottom: 0;
    // override .input-wrap { align-items: flex-start } so trigger fills column
    align-items: stretch;
  }

  .fuel-planner__filter-dropdown {
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .fuel-planner__filter-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0; // buttons otherwise size to label text
    height: 38px;
    min-height: 38px;
    padding: 6px 10px;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    background: #fff;
    font-size: 14px;
    line-height: 1.3;
    color: $black;
    cursor: pointer;
    box-sizing: border-box;
    text-align: left;
    overflow: hidden;

    &:hover {
      border-color: #a8b0ba;
    }

    &--open {
      border-color: #6db534;
    }
  }

  .fuel-planner__brand-filter--searchable .fuel-planner__filter-trigger {
    position: relative;
    cursor: text;
  }

  .fuel-planner__filter-trigger-label {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;

    &--placeholder {
      color: $grey_1;
    }
  }

  .fuel-planner__brand-filter--searchable .fuel-planner__filter-trigger-label {
    pointer-events: none;
  }

  .fuel-planner__filter-trigger-input {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 14px;
    line-height: 1.3;
    color: $black;
    box-sizing: border-box;
    outline: none;
    // Keep focusable under the closed-state label
    position: absolute;
    left: 10px;
    right: 28px;
    top: 0;
    bottom: 0;
    opacity: 0;
    cursor: text;

    &--active {
      position: static;
      opacity: 1;
      right: auto;
    }

    &::placeholder {
      color: $grey_1;
    }
  }

  .fuel-planner__filter-chevron {
    flex-shrink: 0;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #6b7280;
    transition: transform 0.15s ease;

    .fuel-planner__filter-trigger--open & {
      transform: rotate(180deg);
    }
  }

  .fuel-planner__brand-filter--searchable .fuel-planner__filter-chevron {
    position: relative;
    z-index: 1;
    cursor: pointer;
  }

  .fuel-planner__filter-menu {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
  }

  .fuel-planner__filter-menu-item {
    margin: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .fuel-planner__brand-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
      background: rgba(109, 181, 52, 0.08);
    }
  }

  .fuel-planner__brand-option--selected {
    background: rgba(109, 181, 52, 0.08);

    .fuel-planner__brand-option-label {
      color: $black;
      font-weight: 500;
    }
  }

  .fuel-planner__brand-option-checkbox {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1px solid #c1c7cf;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }

  .fuel-planner__brand-option--selected .fuel-planner__brand-option-checkbox {
    border-color: #6db534;
    background: #6db534;
  }

  .fuel-planner__brand-option-label {
    flex: 1;
    min-width: 0;
    color: $black;
    text-align: left;
  }

  .fuel-planner__brand-option--nested {
    padding-left: 28px;
  }

  .fuel-planner__brand-option--partial .fuel-planner__brand-option-checkbox {
    border-color: #6db534;
    background: #6db534;
  }

  .fuel-planner__brand-group-chevron {
    flex-shrink: 0;
    width: 0;
    height: 0;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    border-left: 5px solid #6b7280;
    transition: transform 0.15s ease;

    .fuel-planner__brand-option--expanded & {
      transform: rotate(90deg);
    }
  }

  .fuel-planner__found-title {
    flex: none;
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: $grey_1;
  }

  .fuel-planner__found-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;

    @media screen and (max-width: 1000px) {
      flex: none;
      max-height: 280px;
    }
  }

  .fuel-planner__found-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    border: 1px solid #e6eaee;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.3;
    color: $black;
    cursor: pointer;

    &:hover {
      background: $back;
    }

    &--selected {
      border-color: #f97f20;
      background: rgba(249, 127, 32, 0.06);
    }
  }

  .fuel-planner__found-name {
    font-weight: 600;
  }

  .fuel-planner__found-brand {
    font-size: 14px;
    font-weight: 500;
    color: $grey_2;
  }

  .fuel-planner__found-address {
    font-size: 12px;
    color: $grey_2;
  }

  .fuel-planner__bottom-panels {
    flex: 1;
    display: flex;
    align-items: stretch;
    min-height: 0;
    border-top: 1px solid #e6eaee;

    @media screen and (max-width: 1000px) {
      flex: none;
      flex-direction: column;
      min-height: unset;
    }
  }

  .fuel-planner__selected {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: auto;
    padding: 16px;
    box-sizing: border-box;
    border-right: 1px solid #e6eaee;

    @media screen and (max-width: 1000px) {
      border-right: none;
      border-bottom: 1px solid #e6eaee;
    }
  }

  .fuel-planner__selected-header {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
  }

  .fuel-planner__selected-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: $grey_1;
  }

  .fuel-planner__save-actions {
    flex: none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  .fuel-planner__save-btn {
    flex: none;
    width: auto;
    min-width: 0;
    padding-left: 14px;
    padding-right: 14px;
  }

  .fuel-planner__selected-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .fuel-planner__selected-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    width: 100%;
  }

  .fuel-planner__selected-name {
    font-weight: 600;
  }

  .fuel-planner__selected-brand {
    font-size: 12px;
    color: $grey_2;
  }

  .fuel-planner__selected-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #e6eaee;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.3;
    color: $black;
  }

  .fuel-planner__selected-remove {
    flex: none;
    appearance: none;
    border: none;
    background: transparent;
    color: $orange;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    box-sizing: border-box;
    min-height: 34px;
    height: auto;
    display: inline-flex;
    align-items: center;
    white-space: normal;
    line-height: 1.2;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .fuel-planner__map-panel {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e6eaee;
    position: relative;

    @media screen and (max-width: 1000px) {
      width: 100%;
    }

    &--fullscreen {
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
      align-self: stretch;
    }

    &--form-open {
      .fuel-planner__map-toolbar {
        left: 344px;
        z-index: 4;

        @media screen and (max-width: 1000px) {
          left: 10px;
          right: auto;
          top: 10px;
          flex-direction: row;
        }
      }
    }

    &--region-browse {
      align-self: stretch;
      min-height: 500px;

      @media screen and (max-width: 1000px) {
        min-height: 420px;
      }
    }
  }

  .fuel-planner__map-toolbar {
    position: absolute;
    /* top-right занят NavigationControl MapLibre (+/−) */
    top: 12px;
    left: 12px;
    right: auto;
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media screen and (max-width: 1000px) {
      top: 10px;
      left: 10px;
    }
  }

  .fuel-planner__map-tool-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin: 0;
    padding: 0;
    border: 1px solid #e6eaee;
    border-radius: 8px;
    background: #fff;
    color: $black;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    &:hover {
      border-color: $green;
      color: $green;
    }

    &--active {
      border-color: $green;
      color: $green;
    }
  }

  .fuel-planner__map-legend {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 110px;
    margin: 0;
    padding: 6px 8px;
    border: 1px solid #e6eaee;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    user-select: none;
  }

  .fuel-planner__map-legend-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: $green;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
  }

  .fuel-planner__map-legend-text {
    font-size: 11px;
    line-height: 1.2;
    color: $grey_1;
  }

  .fuel-planner__map {
    width: 100%;
    height: 75%;
    min-height: 75%;
    flex: none;
    z-index: 0;

    @media (max-width: 1000px) {
      height: 500px;
      min-height: 500px;
    }

    .fuel-planner__map-panel--fullscreen &,
    .fuel-planner__map-panel--region-browse & {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }

    :deep(.maplibregl-ctrl-attrib) {
      font-size: 11px;
    }

    :deep(.fuel-planner-map-popup-wrap) {
      pointer-events: none;

      .maplibregl-popup-content {
        padding: 10px 12px;
        border-radius: 8px;
        border: 1px solid #e6eaee;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
        background: #fff;
      }

      .maplibregl-popup-tip {
        border-top-color: #fff;
      }
    }

    :deep(.fuel-planner-map-popup__name) {
      margin: 0 0 2px;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.3;
      color: $black;
    }

    :deep(.fuel-planner-map-popup__brand) {
      margin: 0 0 4px;
      font-size: 12px;
      line-height: 1.3;
      color: $green;
    }

    :deep(.fuel-planner-map-popup__address) {
      margin: 0 0 8px;
      font-size: 12px;
      line-height: 1.35;
      color: $grey_1;
    }

    :deep(.fuel-planner-map-popup__prices) {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    :deep(.fuel-planner-map-popup__price-row) {
      display: flex;
      align-items: baseline;
      gap: 6px;
      font-size: 12px;
      line-height: 1.35;
    }

    :deep(.fuel-planner-map-popup__fuel) {
      flex: 1 1 auto;
      min-width: 0;
      color: $grey_1;
    }

    :deep(.fuel-planner-map-popup__price) {
      flex: 0 0 auto;
      font-weight: 700;
      color: $black;
    }

    :deep(.fuel-planner-map-popup__change) {
      flex: 0 0 auto;
      color: $grey_2;
    }

    :deep(.fuel-planner-map-popup__no-fuels) {
      font-size: 12px;
      color: $grey_2;
    }
  }

  .fuel-planner__map-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 500px;
    min-height: 500px;
    flex: none;
    padding: 24px;
    box-sizing: border-box;

    .fuel-planner__map-panel--region-browse & {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }
    text-align: center;
    color: #6b6c6d;
    font-size: 16px;
    line-height: 1.5;
    background: $back;
  }

  .fuel-planner__confirm-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    box-sizing: border-box;
    background: rgba(26, 26, 24, 0.35);
  }

  .fuel-planner__confirm-modal {
    width: 100%;
    max-width: 360px;
    padding: 20px 20px 16px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    box-sizing: border-box;
  }

  .fuel-planner__confirm-title {
    margin: 0 0 20px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    color: $black;
  }

  .fuel-planner__confirm-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0 0 16px;
  }

  .fuel-planner__confirm-label {
    font-size: 13px;
    font-weight: 600;
    color: $grey_1;
  }

  .fuel-planner__confirm-input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 1px solid #c1c7cf;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.3;
    color: $black;
    background: #fff;

    &:disabled {
      opacity: 0.6;
    }

    &:focus {
      outline: none;
      border-color: #6db534;
    }
  }

  .fuel-planner__confirm-actions {
    display: flex;
    gap: 8px;

    .btn-primary,
    .btn-secondary {
      flex: 1;
      box-sizing: border-box;
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }
    }

    .btn-secondary {
      appearance: none;
      border: 1px solid #c1c7cf;
      border-radius: 4px;
      padding: 10px 16px;
      background: #fff;
      color: $black;
      font-size: 14px;
      font-weight: 600;
    }
  }
</style>
